import { useState, useEffect } from "react";
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Search,
  Upload,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Trash2,
  CheckCircle,
  CloudUpload,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Clock,
  XCircle,
  X
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { format, setHours, setMinutes } from 'date-fns';
import { ASSIGN_CREATIVES_ITEM_QUERY, ASSIGNED_ASSETS_TOCAMPAIGN, ASSIGNED_CONTENT_COUNT, DELETE_CONTENT_MUTATION, GET_ASSIGN_ECOMMERCE_ASSETS, GET_ASSIGNED_CONTENT } from '../Graphql/Queries';
import { ASSIGN_CREATIVES_PACKAGE_INVENTORIES } from '../Graphql/packageQurie';
import client from '../Graphql/apolloClient';
import { generateMockData } from '../Graphql/MockData';

// Backend Interfaces
interface BillboardInfo {
  id: string;
  name: string;
  imageUrl: string;
  format: string;
  resolution: string;
  duration: string;
  selected?: boolean;
  creativesAssigned?: number;
  inventoryType: string;
  dealId: string;
  startDate: string;
  endDate: string;
  inventoryId: string;
  totalPages?: number;
  mediaOwnerId: string;
  mediaOwnerName: string;
  type?: string; // Package or Individual
  parentPackageId?: string; // ID of parent package if this is a package inventory item
  packageBillboards?: Array<{
    id: string;
    name: string;
    creativesUploaded: number;
  }>;
  inventoryDetails: {
    resolution1Width: string;
    resolution1Height: string;
    panel1Width: string;
    panel1Height: string;
    videoSupport: string[];
    imageSupport: string[];
    panelFormat?: string;
  };
}

interface CreativeItem {
  id: string;
  name: string;
  thumbnailUrl: string;
  format: string;
  resolution: string;
  duration: string;
  filePath?: string; // Added for previewing actual media
  selected?: boolean;
  status?: 'pending' | 'approved' | 'rejected';
  comments?: string | null;
  mimeType?: string;
  metadata?: {
    mediaOwnerId: string;
    dealId: string;
  };
}

interface CampaignInfo {
  id: string;
  name: string;
  dealId: string;
  duration: string;
  startDate: string;
  endDate: string;
  status?: string;
}

interface CreativeCount {
  id: string;
  count: number;
}

interface Format {
  type: string;
  format: string;
}

// Status Badge Component
const StatusBadge = ({ status }: { status: 'pending' | 'approved' | 'rejected' | 'PENDING' | 'APPROVED' | 'REJECTED' }) => {
  const normalizedStatus = status.toLowerCase() as 'pending' | 'approved' | 'rejected';

  const statusConfig = {
    pending: {
      icon: Clock,
      text: 'Pending',
      className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    },
    approved: {
      icon: CheckCircle,
      text: 'Approved',
      className: 'bg-green-100 text-green-800 border-green-200'
    },
    rejected: {
      icon: XCircle,
      text: 'Rejected',
      className: 'bg-red-100 text-red-800 border-red-200'
    }
  };

  const config = statusConfig[normalizedStatus] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${config.className}`}>
      <Icon className="h-3 w-3" />
      {config.text}
    </span>
  );
};

// Mock campaign data
const mockCampaign = {
  name: "Samsung s21 ultra",
  dealId: "E - 2023-06120000004",
  duration: "01 Jun 2023 - 03 Jun 2023"
};

interface CreativeManagementProps {
  base_path?: string;
}

const _inventoryTypeDisplayMapping: { [key: string]: string } = {
  'digital': 'Digital',
  'digital_billboard': 'Digital',
  'classic': 'Classic',
  'static': 'Classic',
  'static_billboard': 'Classic',
  'deal': 'Deal'
};

const isClassicInventory = (inventoryType: string) => {
  if (!inventoryType) return false;
  const type = inventoryType.toLowerCase();
  return type === 'classic' || type === 'static_billboard' || type === 'static';
};

const isDigitalInventory = (inventoryType: string) => {
  if (!inventoryType) return false;
  const type = inventoryType.toLowerCase();
  return type === 'digital' || type === 'digital_billboard';
};

// Assignment Context Metadata Interface
interface AssignmentMetadata {
  assignedAsStandalone: boolean;
  packageId?: string;
  timestamp: number;
}

// LocalStorage helper functions for assignment metadata
const ASSIGNMENT_METADATA_KEY = 'creative_assignment_metadata';

const getAssignmentMetadataKey = (campaignId: string, inventoryId: string, creativeId: string): string => {
  return `${campaignId}_${inventoryId}_${creativeId}`;
};

const getAssignmentMetadata = (campaignId: string): Record<string, AssignmentMetadata> => {
  try {
    const stored = localStorage.getItem(`${ASSIGNMENT_METADATA_KEY}_${campaignId}`);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading assignment metadata:', error);
    return {};
  }
};

const saveAssignmentMetadata = (
  campaignId: string,
  inventoryId: string,
  creativeId: string,
  isStandalone: boolean,
  packageId?: string
): void => {
  try {
    const metadata = getAssignmentMetadata(campaignId);
    const key = getAssignmentMetadataKey(campaignId, inventoryId, creativeId);
    metadata[key] = {
      assignedAsStandalone: isStandalone,
      packageId: packageId,
      timestamp: Date.now()
    };
    localStorage.setItem(`${ASSIGNMENT_METADATA_KEY}_${campaignId}`, JSON.stringify(metadata));
  } catch (error) {
    console.error('Error saving assignment metadata:', error);
  }
};

const getCreativeAssignmentContext = (
  campaignId: string,
  inventoryId: string,
  creativeId: string
): AssignmentMetadata | null => {
  const metadata = getAssignmentMetadata(campaignId);
  const key = getAssignmentMetadataKey(campaignId, inventoryId, creativeId);
  return metadata[key] || null;
};

const deleteAssignmentMetadata = (
  campaignId: string,
  inventoryId: string,
  creativeId: string
): void => {
  try {
    const metadata = getAssignmentMetadata(campaignId);
    const key = getAssignmentMetadataKey(campaignId, inventoryId, creativeId);
    delete metadata[key];
    localStorage.setItem(`${ASSIGNMENT_METADATA_KEY}_${campaignId}`, JSON.stringify(metadata));
  } catch (error) {
    console.error('Error deleting assignment metadata:', error);
  }
};

export default function CreativeManagement({ base_path }: CreativeManagementProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaign') || '1';
  // const fileInputRef = useRef<HTMLInputElement>(null); // Currently unused

  // Backend state
  const [campaign, setCampaign] = useState<CampaignInfo | null>(null);
  const [billboards, setBillboards] = useState<BillboardInfo[]>([]);
  const [filteredBillboards, setFilteredBillboards] = useState<BillboardInfo[]>([]);
  const [creatives, setCreatives] = useState<CreativeItem[]>([]);
  const [assignedCreatives, setAssignedCreatives] = useState<Map<string, CreativeItem[]>>(new Map());
  const [approvedCreativeCount, setApprovedCreativeCount] = useState<CreativeCount[]>([]);
  const [packageBillboardCounts, setPackageBillboardCounts] = useState<{ [key: string]: CreativeCount[] }>({});
  const [formats, setFormats] = useState<Format[]>([]);
  const [resolutions, setResolutions] = useState<string[]>([]);
  const [durations, setDurations] = useState<string[]>([]);
  const [inventoryTypes, setInventoryTypes] = useState<string[]>([]);
  const [packageInventories, setPackageInventories] = useState<{ [key: string]: any[] }>({});
  const [packageInventorySearch, setPackageInventorySearch] = useState<string>("");
  // Pagination states (currently unused - implement when pagination is needed)
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [popupCurrentPage, setPopupCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [popupTotalPages, setPopupTotalPages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Pagination state for uploaded files
  const [uploadedFilesCurrentPage, setUploadedFilesCurrentPage] = useState<number>(1);
  const [uploadedFilesPerPage] = useState<number>(5);

  // Pagination state for assign creatives
  const [assignCreativesCurrentPage, setAssignCreativesCurrentPage] = useState<number>(1);
  const [assignCreativesPerPage] = useState<number>(50); // 6 cards per page

  // Existing UI state
  const [searchBillboard, setSearchBillboard] = useState("");
  const [searchCreative, setSearchCreative] = useState("");
  const [searchCreativeTerm, setSearchCreativeTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState<string>(''); // For API search
  const [selectedBillboards, setSelectedBillboards] = useState<string[]>([]);
  const [selectedCreatives, setSelectedCreatives] = useState<string[]>([]);
  const [formatFilter, setFormatFilter] = useState("all");
  const [resolutionFilter, setResolutionFilter] = useState("all");
  const [durationFilter, setDurationFilter] = useState("all");
  const [inventoryTypeFilter, setInventoryTypeFilter] = useState("all");
  const [popupSearchInput, setPopupSearchInput] = useState<string>('');
  const [popupSearchTerm, setPopupSearchTerm] = useState<string>('');
  const [filteredPopupCreatives, setFilteredPopupCreatives] = useState<CreativeItem[] | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreativeModal, setShowCreativeModal] = useState(false);
  const [selectedCreativeForView, setSelectedCreativeForView] = useState<CreativeItem | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showEditModal, setShowEditModal] = useState(false);
  // const [editingBillboard, setEditingBillboard] = useState<any>(null); // Currently unused
  const [expandedPackages, setExpandedPackages] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  // New states for billboard assignment functionality
  const [selectedBillboard, setSelectedBillboard] = useState<BillboardInfo | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAssignedCreativesModal, setShowAssignedCreativesModal] = useState(false);
  const [billboardAssignedCreatives, setBillboardAssignedCreatives] = useState<CreativeItem[]>([]);
  const [filteredBillboardAssignedCreatives, setFilteredBillboardAssignedCreatives] = useState<CreativeItem[]>([]);
  const [selectedCreativesForAssign, setSelectedCreativesForAssign] = useState<string[]>([]);

  // Rejection reason modal state
  const [showRejectionReasonModal, setShowRejectionReasonModal] = useState(false);
  const [selectedRejectionReason, setSelectedRejectionReason] = useState<string>('');

  // Authentication and environment variables
  const token = localStorage.getItem('authToken');
  const companyId = import.meta.env.VITE_APP_COMPANY_ID || 'default_company_id';
  const userId = localStorage.getItem('userId');

  // Add these states at the top with other states:
  const [assignTab, setAssignTab] = useState<'list' | 'preview'>('list');
  const [previewFile, setPreviewFile] = useState<any>(null);


  // GraphQL Queries
  const { loading: billboardsLoading, data: billboardsData, refetch: refetchBillboards } = useQuery(
    ASSIGN_CREATIVES_ITEM_QUERY,
    {
      client,
      variables: {
        accessToken: token || '',
        id: campaignId || '',
        searchTerm: searchTerm,
        page: String(currentPage - 1) // Convert to 0-based index for API
      },
      skip: !token || !campaignId || import.meta.env.VITE_APP_MOCK_DATA === 'true',
      onError: (err) => {
        console.error('Fetch Billboards Error:', err);

        toast({
          title: "Error",
          description: "An unexpected error occurred while fetching billboards",
          variant: "destructive",
        });
      }
    }
  );

  const { data: ecommerceAssetsData, refetch: refetchEcommerceAssets } = useQuery(
    GET_ASSIGN_ECOMMERCE_ASSETS,
    {
      client,
      variables: {
        accessToken: token || '',
        companyId,
        userId: userId || '',
        page: popupCurrentPage - 1, // Convert to 0-based index for API
        resolution:
          selectedBillboard && ["classic", "static"].includes(selectedBillboard.inventoryType?.toLowerCase())
            ? ""
            : resolutionFilter !== 'all' && resolutionFilter
              ? resolutionFilter.replace(/X/g, 'x').replace(/\s+/g, '')
              : (selectedBillboard && selectedBillboard.resolution)
                ? selectedBillboard.resolution.replace(/X/g, 'x').replace(/\s+/g, '')
                : '',
        size: 50,
        duration: undefined,
        searchTerm: popupSearchTerm.length >= 3 ? popupSearchTerm : ''
      },
      skip: !token || (!showUploadModal && !showAssignModal) || import.meta.env.VITE_APP_MOCK_DATA === 'true',
      onError: (err) => {
        console.error('Fetch Ecommerce Assets Error:', err);
        toast({
          title: "Error",
          description: "An unexpected error occurred while fetching ecommerce assets",
          variant: "destructive",
        });
      },
      onCompleted: (data) => {
        console.info('GET_ASSIGN_ECOMMERCE_ASSETS completed:', {
          hasData: !!data,
          assetsCount: data?.ecommerceAssets?.length || 0
        });
      }
    }
  );

  const { data: contentCountData } = useQuery(
    ASSIGNED_CONTENT_COUNT,
    {
      client,
      variables: {
        accessToken: token || '',
        billboardIds: billboards.map(b => b.inventoryId),
        companyId,
        campaignId: campaignId || ''
      },
      skip: !token || !campaignId || billboards.length === 0 || import.meta.env.VITE_APP_MOCK_DATA === 'true',
      onError: (err) => {
        console.error('Fetch Content Count Error:', err);
        toast({
          title: "Error",
          description: "An unexpected error occurred while fetching content count",
          variant: "destructive",
        });
      }
    }
  );

  // Lazy query for package inventories
  const [getPackageInventories, { data: packageInventoriesData, loading: packageInventoriesLoading }] = useLazyQuery(
    ASSIGN_CREATIVES_PACKAGE_INVENTORIES,
    {
      client,
      onError: (err) => {
        console.error('Fetch Package Inventories Error:', err);
        toast({
          title: "Error",
          description: "An unexpected error occurred while fetching package inventories",
          variant: "destructive",
        });
      }
    }
  );

  // Lazy query for package billboard counts
  const [getPackageBillboardCounts] = useLazyQuery(
    ASSIGNED_CONTENT_COUNT,
    {
      client,
      onError: (err) => {
        console.error('Fetch Package Billboard Counts Error:', err);
        toast({
          title: "Error",
          description: "An unexpected error occurred while fetching package billboard counts",
          variant: "destructive",
        });
      }
    }
  );

  // GraphQL Mutations
  const [assignAssetToCampaign] = useMutation(ASSIGNED_ASSETS_TOCAMPAIGN, {
    client,
    // onError: (err) => {
    //   console.error('Assign Asset Error:', err);
    //   toast({
    //     title: "Error",
    //     description: "An unexpected error occurred while assigning assets",
    //     variant: "destructive",
    //   });
    // }
  });

  const [deleteContent] = useMutation(DELETE_CONTENT_MUTATION, {
    client,
    onError: (err) => {
      console.error('Delete Content Error:', err);
      toast({
        title: "Error",
        description: "Failed to delete content",
        variant: "destructive",
      });
    }
  });

  // Add lazy query for assigned content
  const [getAssignedContent] = useLazyQuery(GET_ASSIGNED_CONTENT, {
    client,
    onError: (err) => {
      console.error('Get Assigned Content Error:', err);
    }
  });

  // Initialize data with mock or API
  useEffect(() => {
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      generateMockData(ASSIGN_CREATIVES_ITEM_QUERY).then((mockResponse: any) => {
        const data = mockResponse.data;
        const creativeItems = data.assignCreativesItem;
        const formattedBillboards = creativeItems.map((item: any) => ({
          id: item.id || 'unknown-id',
          name: item.inventoryName || 'Unknown',
          imageUrl: item.inventoryThumbnailUrl && item.inventoryThumbnailUrl !== 'null' ? item.inventoryThumbnailUrl : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
          // FIX: Use helper function to check for classic/static types
          format: isClassicInventory(item.inventoryType) && item.inventoryDetails?.panelFormat
            ? item.inventoryDetails.panelFormat
            : [...(item.inventoryDetails?.videoSupport || []), ...(item.inventoryDetails?.imageSupport || [])].join(', ').toUpperCase() || 'N/A',
          resolution: item.inventoryDetails?.resolution1Width && item.inventoryDetails?.resolution1Height
            ? `${item.inventoryDetails.resolution1Width} X ${item.inventoryDetails.resolution1Height}`
            : item.inventoryDetails?.panel1Width && item.inventoryDetails?.panel1Height
              ? `${item.inventoryDetails.panel1Width} X ${item.inventoryDetails.panel1Height}`
              : 'N/A',
          duration: item.duration ? String(item.duration) : 'N/A',
          inventoryType: item.inventoryType || 'N/A',
          dealId: item.dealId || 'N/A',
          startDate: item.startDate?.date || '',
          endDate: item.endDate?.date || '',
          inventoryId: item.inventoryId || 'N/A',
          totalPages: item.totalPages || 1,
          mediaOwnerId: item.mediaOwnerId || 'N/A',
          mediaOwnerName: item.mediaOwnerName || 'N/A',
          inventoryDetails: item.inventoryDetails || {
            resolution1Width: '',
            resolution1Height: '',
            panel1Width: '',
            panel1Height: '',
            videoSupport: [],
            imageSupport: [],
            panelFormat: ''
          }
        }));
        setBillboards(formattedBillboards);
        setFilteredBillboards(formattedBillboards);
        setTotalPages(creativeItems[0]?.totalPages || 1);

        // Set campaign info from mock data
        setCampaign({
          id: campaignId,
          name: creativeItems[0]?.campaignName || mockCampaign.name,
          dealId: creativeItems[0]?.dealId || mockCampaign.dealId,
          duration: creativeItems[0]?.duration || mockCampaign.duration,
          startDate: creativeItems[0]?.startDate?.date || '',
          endDate: creativeItems[0]?.endDate?.date || '',
          status: creativeItems[0]?.campaignStatus || ''
        });

        const allFormats = creativeItems.flatMap((item: any) => {
          if (item.inventoryType.toLowerCase() === 'classic' && item.inventoryDetails?.panelFormat) {
            return [{ type: "panel", format: item.inventoryDetails.panelFormat }];
          }
          return [
            ...(item.inventoryDetails?.videoSupport ? item.inventoryDetails.videoSupport.map((format: string) => ({ type: "video", format })) : []),
            ...(item.inventoryDetails?.imageSupport ? item.inventoryDetails.imageSupport.map((format: string) => ({ type: "image", format })) : [])
          ];
        });
        const uniqueFormats = Array.from(
          new Map(allFormats.map((item: Format) => [`${item.type}-${item.format}`, item])).values()
        ) as Format[];
        setFormats(uniqueFormats);

        const uniqueResolutions: string[] = Array.from(
          new Set(
            creativeItems.map((item: any) => {
              const { resolution1Width, resolution1Height, panel1Width, panel1Height } = item.inventoryDetails || {};
              let resolutionText = resolution1Width && resolution1Height ? `${resolution1Width} x ${resolution1Height}` : '';
              let panelText = panel1Width && panel1Height ? `${panel1Width} x ${panel1Height}` : '';
              if (resolutionText && panelText) return `${resolutionText}, ${panelText}`;
              return resolutionText || panelText || 'Resolution/Panel Not Available';
            })
          )
        );
        setResolutions(uniqueResolutions);

        const uniqueDurations: string[] = Array.from(
          new Set(
            creativeItems.map((item: any) => {
              const duration = item.duration ? String(item.duration) : 'N/A';
              return duration;
            })
          )
        );
        setDurations(uniqueDurations);

        const uniqueInventoryTypes: string[] = Array.from(
          new Set(
            creativeItems.map((item: any) => {
              return item.inventoryType || 'Unknown';
            })
          )
        );
        setInventoryTypes(uniqueInventoryTypes);

        // Set default inventory type filter based on available types
        if (uniqueInventoryTypes.length === 1 && uniqueInventoryTypes[0].toLowerCase() === 'digital') {
          setInventoryTypeFilter('digital');
        } else if (uniqueInventoryTypes.length === 1 && uniqueInventoryTypes[0].toLowerCase() === 'classic') {
          setInventoryTypeFilter('classic');
        }
      }).catch(err => {
        console.error('Error generating mock data:', err);
        toast({
          title: "Error",
          description: "Failed to load mock data",
          variant: "destructive",
        });
      });

      generateMockData(GET_ASSIGN_ECOMMERCE_ASSETS).then((mockResponse: any) => {
        const data = mockResponse.data;
        const formattedCreatives = data.ecommerceAssets.map((asset: any) => ({
          id: asset.id || 'unknown-id',
          name: asset.fileName || 'Unknown',
          thumbnailUrl: asset.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
          format: asset.mimeType || 'N/A',
          resolution: asset.resolution || 'N/A',
          duration: asset.duration ? String(asset.duration) : 'N/A',
          mimeType: asset.mimeType || '',
          filePath: asset.filePath || '',
          metadata: asset.metadata || {}
        }));
        const uniqueCreatives = Array.from(
          new Map(formattedCreatives.map((creative: CreativeItem) => [creative.id, creative])).values()
        ) as CreativeItem[];
        setCreatives(uniqueCreatives);
        setPopupTotalPages(Math.ceil(data.ecommerceAssets[0]?.totalPages || 1));
      });

      generateMockData(ASSIGNED_CONTENT_COUNT).then((mockResponse: any) => {
        setApprovedCreativeCount(mockResponse.data.assignedContentCount || []);
      });

      // Load mock package inventories for package billboards
      generateMockData(ASSIGN_CREATIVES_PACKAGE_INVENTORIES).then((mockResponse: any) => {
        const packageInventoriesData = mockResponse.data.assaigncreativesPackageInventories || [];
        if (packageInventoriesData.length > 0) {
          // Group by packageId and set package inventories
          const packageGroups = packageInventoriesData.reduce((acc: any, inventory: any) => {
            const packageId = inventory.packageId;
            if (!acc[packageId]) acc[packageId] = [];
            acc[packageId].push(inventory);
            return acc;
          }, {});

          // Find corresponding billboards and set their package inventories
          Object.entries(packageGroups).forEach(([packageId, inventories]: [string, any]) => {
            const billboard = billboards.find((b: any) => b.inventoryId === packageId);
            if (billboard) {
              setPackageInventories(prev => ({
                ...prev,
                [billboard.id]: inventories
              }));
            }
          });
        }
      }).catch(err => {
        console.error('Error loading mock package inventories:', err);
      });
    } else {
      if (billboardsData?.assignCreativesItem && billboardsData.assignCreativesItem.length > 0) {
        setCampaign({
          id: campaignId || '',
          name: billboardsData.assignCreativesItem[0]?.campaignName || 'Campaign Name',
          dealId: billboardsData.assignCreativesItem[0]?.dealId || 'Deal ID',
          duration: billboardsData.assignCreativesItem[0]?.duration || 'Duration',
          startDate: billboardsData.assignCreativesItem[0]?.startDate?.date || '',
          endDate: billboardsData.assignCreativesItem[0]?.endDate?.date || '',
          status: billboardsData.assignCreativesItem[0]?.campaignStatus || ''
        });
      }
    }
  }, [campaignId, billboardsData]);

  // Update billboards when data is fetched
  useEffect(() => {
    if (billboardsData?.assignCreativesItem && !billboardsLoading) {
      const formattedBillboards = billboardsData.assignCreativesItem.map((item: any) => ({
        id: item.id || 'unknown-id',
        name: item.inventoryName || 'Unknown',
        imageUrl: item.inventoryThumbnailUrl && item.inventoryThumbnailUrl !== 'null' ? item.inventoryThumbnailUrl : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
        // FIX: Use helper function to check for classic/static types
        format: isClassicInventory(item.inventoryType) && item.inventoryDetails?.panelFormat
          ? item.inventoryDetails.panelFormat
          : [...(item.inventoryDetails?.videoSupport || []), ...(item.inventoryDetails?.imageSupport || [])].join(', ').toUpperCase() || 'N/A',
        resolution: item.inventoryDetails?.resolution1Width && item.inventoryDetails?.resolution1Height
          ? `${item.inventoryDetails.resolution1Width} X ${item.inventoryDetails.resolution1Height}`
          : item.inventoryDetails?.panel1Width && item.inventoryDetails?.panel1Height
            ? `${item.inventoryDetails.panel1Width} X ${item.inventoryDetails.panel1Height}`
            : 'N/A',
        duration: item.duration ? String(item.duration) : 'N/A',
        inventoryType: item.inventoryType || 'N/A',
        dealId: item.dealId || 'N/A',
        startDate: item.startDate?.date || '',
        endDate: item.endDate?.date || '',
        inventoryId: item.inventoryId || 'N/A',
        mediaOwnerId: item.mediaOwnerId || 'N/A',
        mediaOwnerName: item.mediaOwnerName || 'N/A',
        totalPages: item.totalPages || 1,
        inventoryDetails: item.inventoryDetails || {
          resolution1Width: '',
          resolution1Height: '',
          panel1Width: '',
          panel1Height: '',
          videoSupport: [],
          imageSupport: [],
          panelFormat: ''
        }
      }));
      setBillboards(formattedBillboards);
      setFilteredBillboards(formattedBillboards);
      setTotalPages(formattedBillboards[0]?.totalPages || 1);

      const allFormats = formattedBillboards.flatMap((item: BillboardInfo) => {
        if (item.inventoryType.toLowerCase() === 'classic' && item.inventoryDetails?.panelFormat) {
          return [{ type: "panel", format: item.inventoryDetails.panelFormat }];
        }
        return [
          ...(item.inventoryDetails?.videoSupport ? item.inventoryDetails.videoSupport.map((format: string) => ({ type: "video", format })) : []),
          ...(item.inventoryDetails?.imageSupport ? item.inventoryDetails.imageSupport.map((format: string) => ({ type: "image", format })) : [])
        ];
      });
      const uniqueFormats = Array.from(
        new Map(allFormats.map((item: Format) => [`${item.type}-${item.format}`, item])).values()
      ) as Format[];
      setFormats(uniqueFormats);

      const uniqueResolutions: string[] = Array.from(
        new Set(
          formattedBillboards.map((item: BillboardInfo) => {
            const { resolution1Width, resolution1Height, panel1Width, panel1Height } = item.inventoryDetails;
            let resolutionText = resolution1Width && resolution1Height ? `${resolution1Width} x ${resolution1Height}` : '';
            let panelText = panel1Width && panel1Height ? `${panel1Width} x ${panel1Height}` : '';
            if (resolutionText && panelText) return `${resolutionText}, ${panelText}`;
            return resolutionText || panelText || 'Resolution/Panel Not Available';
          })
        )
      );
      setResolutions(uniqueResolutions);

      const uniqueDurations: string[] = Array.from(
        new Set(
          formattedBillboards.map((item: BillboardInfo) => {
            const duration = item.duration ? String(item.duration) : 'N/A';
            return duration;
          })
        )
      );
      setDurations(uniqueDurations);

      const uniqueInventoryTypes: string[] = Array.from(
        new Set(
          formattedBillboards.map((item: BillboardInfo) => {
            return item.inventoryType || 'Unknown';
          })
        )
      );

      // Only update inventoryTypes if it's empty or the values have changed
      if ((!searchTerm) && (inventoryTypes.length === 0 || JSON.stringify(inventoryTypes.sort()) !== JSON.stringify(uniqueInventoryTypes.sort()))) {
        setInventoryTypes(uniqueInventoryTypes);
      }

      // Set default inventory type filter based on available types
      if (uniqueInventoryTypes.length === 1 && uniqueInventoryTypes[0].toLowerCase() === 'digital') {
        setInventoryTypeFilter('digital');
      } else if (uniqueInventoryTypes.length === 1 && uniqueInventoryTypes[0].toLowerCase() === 'classic') {
        setInventoryTypeFilter('classic');
      }
    }
  }, [billboardsData, billboardsLoading]);

  // Update creatives when ecommerce assets are fetched
  useEffect(() => {
    if (ecommerceAssetsData?.ecommerceAssets) {
      const formattedCreatives = ecommerceAssetsData.ecommerceAssets.map((asset: any) => ({
        id: asset.id || 'unknown-id',
        name: asset.fileName || 'Unknown',
        thumbnailUrl: asset.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
        format: asset.mimeType || 'N/A',
        resolution: asset.resolution || 'N/A',
        duration: asset.duration ? String(asset.duration) : 'N/A',
        mimeType: asset.mimeType || '',
        filePath: asset.filePath || '', // <-- ensure filePath is included from API
        metadata: asset.metadata || {},
      }));

      const uniqueCreatives = Array.from(
        new Map(formattedCreatives.map((creative: CreativeItem) => [creative.id, creative])).values()
      ) as CreativeItem[];

      setCreatives(uniqueCreatives);
      setPopupTotalPages(Math.ceil(ecommerceAssetsData.ecommerceAssets[0]?.totalPages || 1));
    }
  }, [ecommerceAssetsData, showAssignModal]);

  // Update creative counts
  useEffect(() => {
    if (contentCountData?.assignedContentCount) {
      setApprovedCreativeCount(contentCountData.assignedContentCount || []);
    }
  }, [contentCountData]);

  // Update package inventories
  useEffect(() => {
    if (packageInventoriesData?.assaigncreativesPackageInventories) {
      const inventories = packageInventoriesData.assaigncreativesPackageInventories || [];
      // Find the billboard that corresponds to this package data
      if (inventories.length > 0) {
        const packageId = inventories[0].packageId;
        // Find the billboard with this inventoryId (packageId)
        const billboard = billboards.find(b => b.inventoryId === packageId);

        if (billboard) {
          setPackageInventories(prev => ({
            ...prev,
            [billboard.id]: inventories // Use billboard.id as key, not packageId
          }));

          // Fetch assigned content counts for package billboards
          const packageBillboardIds = inventories.map((inv: any) => inv.inventoryId).filter(Boolean);
          if (packageBillboardIds.length > 0 && import.meta.env.VITE_APP_MOCK_DATA !== 'true') {
            getPackageBillboardCounts({
              variables: {
                accessToken: token || '',
                billboardIds: packageBillboardIds,
                companyId,
                campaignId: campaignId || ''
              }
            }).then(({ data }) => {
              if (data?.assignedContentCount) {
                setPackageBillboardCounts(prev => ({
                  ...prev,
                  [billboard.id]: data.assignedContentCount
                }));
              }
            }).catch(err => {
              console.error('Error fetching package billboard counts:', err);
            });
          }
        }
      }
    }
  }, [packageInventoriesData, billboards, token, campaignId, companyId, getPackageBillboardCounts]);

  // Auto-load package inventories for all package billboards on page load
  useEffect(() => {
    if (billboards.length > 0 && import.meta.env.VITE_APP_MOCK_DATA !== 'true') {
      // Find all package billboards
      const packageBillboards = billboards.filter(billboard =>
        billboard.type === 'Package' ||
        billboard.inventoryType?.toLowerCase() === 'package' ||
        (billboard.packageBillboards?.length ?? 0) > 0
      );

      // Load package inventories for each package billboard that hasn't been loaded yet
      packageBillboards.forEach(billboard => {
        if (!packageInventories[billboard.id] && billboard.inventoryId) {
          getPackageInventories({
            variables: {
              accessToken: token || '',
              packageId: billboard.inventoryId,
              page: 0,
              size: 999
            }
          });
        }
      });
    }
  }, [billboards, packageInventories, token, getPackageInventories]);

  // Refetch billboards when page changes
  useEffect(() => {
    if (campaignId && currentPage > 0) {
      refetchBillboards();
    }
  }, [currentPage, campaignId, refetchBillboards]);

  // Refetch ecommerce assets when popup page changes
  useEffect(() => {
    if ((showUploadModal || showAssignModal) && popupCurrentPage > 0) {
      refetchEcommerceAssets();
    }
  }, [popupCurrentPage, showUploadModal, showAssignModal, refetchEcommerceAssets]);

  // Refetch ecommerce assets when selectedBillboard changes and assign modal is open
  useEffect(() => {
    if (showAssignModal && selectedBillboard && import.meta.env.VITE_APP_MOCK_DATA !== 'true') {

      const formattedResolution = selectedBillboard.resolution && selectedBillboard.resolution !== 'N/A'
        ? selectedBillboard.resolution.replace(/X/g, 'x').replace(/\s+/g, '')
        : '';

      const isClassicOrStatic = selectedBillboard.inventoryType && ["classic", "static"].includes(selectedBillboard.inventoryType.toLowerCase());

      refetchEcommerceAssets({
        accessToken: token || '',
        companyId,
        userId: userId || '',
        page: popupCurrentPage - 1,
        resolution: isClassicOrStatic ? "" : formattedResolution,
        size: 50,
        searchTerm: popupSearchTerm.length >= 3 ? popupSearchTerm : ''
      });
    }
  }, [selectedBillboard, showAssignModal, refetchEcommerceAssets, token, companyId, userId, popupCurrentPage, popupSearchTerm]);

  // Reset pagination when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Reset popup pagination when popup search term changes
  useEffect(() => {
    setPopupCurrentPage(1);
  }, [popupSearchTerm]);

  // Apply local filters for format, resolution, duration, and search
  useEffect(() => {
    try {
      let filtered = [...billboards];

      // Apply search filter first
      if (searchTerm && searchTerm.trim()) {
        filtered = filtered.filter(billboard =>
          billboard.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          billboard.inventoryId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          billboard.dealId.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      if (formatFilter !== 'all') {
        filtered = filtered.filter(billboard => {
          if (billboard.inventoryType.toLowerCase() === 'classic' && billboard.inventoryDetails.panelFormat) {
            return billboard.inventoryDetails.panelFormat.toLowerCase().includes(formatFilter.toLowerCase());
          }
          const format = billboard.format || 'N/A';
          return format.toLowerCase().includes(formatFilter.toLowerCase());
        });
      }

      if (resolutionFilter !== 'all') {
        filtered = filtered.filter(billboard => {
          const resolution = billboard.resolution || 'N/A';
          return resolution.toLowerCase().includes(resolutionFilter.toLowerCase());
        });
      }

      if (durationFilter !== 'all') {
        filtered = filtered.filter(billboard => {
          const duration = billboard.duration || 'N/A';
          const normalizedFilterDuration = String(durationFilter);
          return duration.toLowerCase().includes(normalizedFilterDuration.toLowerCase());
        });
      }

      if (inventoryTypeFilter !== 'all') {
        filtered = filtered.filter(billboard => {
          const inventoryType = billboard.inventoryType || 'Unknown';
          return inventoryType.toLowerCase() === inventoryTypeFilter.toLowerCase();
        });
      }

      setFilteredBillboards(filtered);
    } catch (error) {
      console.error('Error in filter useEffect:', error);
      setFilteredBillboards(billboards);
      toast({
        title: "Error",
        description: "An error occurred while applying filters. Showing unfiltered results.",
        variant: "destructive",
      });
    }
  }, [billboards, formatFilter, resolutionFilter, durationFilter, inventoryTypeFilter, searchTerm]);

  // Refetch data when page changes
  useEffect(() => {
    if (currentPage > 0) {
      refetchBillboards();
    }
  }, [currentPage, refetchBillboards]);

  // Reset pagination when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);


  // Helper function to get all billboards (main + package sub-billboards) by ID
  const getAllBillboardsById = (ids: string[]) => {
    const result: BillboardInfo[] = [];

    for (const id of ids) {
      // First check main billboards
      const mainBillboard = billboards.find(b => b.id === id);
      if (mainBillboard) {
        result.push(mainBillboard);
        continue;
      }

      // Check if this is a package inventory ID - search through packageInventories
      let foundPackageInventory = false;
      for (const [packageBillboardId, inventories] of Object.entries(packageInventories)) {
        const packageInventory = inventories.find(inv => inv.inventoryId === id);
        if (packageInventory) {
          // Find the parent package billboard
          const parentBillboard = billboards.find(b => b.id === packageBillboardId);
          if (parentBillboard) {
            // Create a proper billboard object from the package inventory
            const packageBillboard: BillboardInfo = {
              ...parentBillboard,
              id: packageInventory.inventoryId,
              name: packageInventory.name,
              inventoryId: packageInventory.inventoryId,
              parentPackageId: parentBillboard.inventoryId, // Set parent package ID
              inventoryType: packageInventory.type || 'digital',
              resolution: packageInventory.specification?.resolution1Width && packageInventory.specification?.resolution1Height
                ? `${packageInventory.specification.resolution1Width} X ${packageInventory.specification.resolution1Height}`
                : packageInventory.size || parentBillboard.resolution || 'N/A',
              duration: String(packageInventory.spotDuration || parentBillboard.duration || 'N/A'),
              imageUrl: packageInventory.thumbnail || parentBillboard.imageUrl,
              format: packageInventory.type?.toLowerCase() === 'digital'
                ? [...(packageInventory.videoSupport || []), ...(packageInventory.imageSupport || [])].join(', ').toUpperCase() || 'DIGITAL'
                : packageInventory.type?.toLowerCase() === 'classic'
                  ? (packageInventory.imageSupport || []).join(', ').toUpperCase() || 'N/A'
                  : packageInventory.type?.toUpperCase() || parentBillboard.format || 'N/A',
              mediaOwnerId: packageInventory.mediaOwnerId || parentBillboard.mediaOwnerId,
              mediaOwnerName: packageInventory.mediaOwnerName || parentBillboard.mediaOwnerName,
              dealId: parentBillboard.dealId,
              startDate: parentBillboard.startDate,
              endDate: parentBillboard.endDate,
              inventoryDetails: {
                resolution1Width: packageInventory.specification?.resolution1Width || '',
                resolution1Height: packageInventory.specification?.resolution1Height || '',
                panel1Width: packageInventory.specification?.panel1Width || '',
                panel1Height: packageInventory.specification?.panel1Height || '',
                videoSupport: packageInventory.videoSupport || [],
                imageSupport: packageInventory.imageSupport || [],
                panelFormat: packageInventory.panelFormat || parentBillboard.inventoryDetails?.panelFormat || ''
              }
            };
            result.push(packageBillboard);
            foundPackageInventory = true;
            break;
          }
        }
      }

      // If still not found, check legacy packageBillboards (for fallback)
      if (!foundPackageInventory) {
        for (const mainBillboard of billboards) {
          if (mainBillboard.packageBillboards) {
            const packageBillboard = mainBillboard.packageBillboards.find(pb => pb.id === id);
            if (packageBillboard) {
              result.push({
                ...packageBillboard,
                format: mainBillboard.format,
                resolution: mainBillboard.resolution,
                duration: mainBillboard.duration,
                inventoryType: mainBillboard.inventoryType,
                dealId: mainBillboard.dealId,
                startDate: mainBillboard.startDate,
                endDate: mainBillboard.endDate,
                inventoryId: id,
                imageUrl: 'https://images.unsplash.com/photo-1570993492881-25240ce854f4?w=60&h=40&fit=crop',
                totalPages: mainBillboard.totalPages,
                inventoryDetails: mainBillboard.inventoryDetails,
                mediaOwnerId: mainBillboard.mediaOwnerId,
                mediaOwnerName: mainBillboard.mediaOwnerName
              } as BillboardInfo);
              break;
            }
          }
        }
      }
    }

    return result;
  };

  // Helper function to get context-aware assigned creative count
  const getContextAwareCreativeCount = (inventoryId: string, isPackageInventory: boolean, packageId?: string): number => {
    // 1. First, get the ACTUAL count from API data (primary source)
    let apiCount = 0;

    if (isPackageInventory && packageId) {
      // For package inventories, check packageBillboardCounts
      const packageCounts = packageBillboardCounts[packageId];
      if (packageCounts) {
        const countData = packageCounts.find(item => item.id === inventoryId);
        apiCount = countData?.count || 0;
      }
    } else {
      // For standalone inventories, check approvedCreativeCount
      const countData = approvedCreativeCount.find(item => item.id === inventoryId);
      apiCount = countData?.count || 0;
    }

    // 2. If no API data, return 0 (no creatives assigned)
    if (apiCount === 0) {
      return 0;
    }

    // 3. Get metadata to filter by context (if available)
    const allMetadata = getAssignmentMetadata(campaignId || '');
    const metadataKeys = Object.keys(allMetadata).filter(key => key.includes(inventoryId));

    // 4. If no metadata exists, return API count (legacy assignments or no localStorage)
    // This handles cases where content was assigned before metadata system or localStorage was cleared
    if (metadataKeys.length === 0) {
      // For both package and standalone inventories without metadata, return API count
      // This handles legacy assignments made before the metadata system was implemented
      return apiCount;
    }

    // 5. Count based on metadata context (use metadata to filter API count)
    let count = 0;
    Object.entries(allMetadata).forEach(([key, metadata]) => {
      // Check if this metadata entry is for the current inventory
      if (key.includes(inventoryId)) {
        if (isPackageInventory && packageId) {
          // For package inventories: count only creatives assigned to this specific package
          if (metadata.packageId === packageId) {
            count++;
          }
        } else if (!isPackageInventory) {
          // For standalone inventories: count only creatives assigned as standalone
          if (metadata.assignedAsStandalone === true) {
            count++;
          }
        }
      }
    });

    // 6. Return the count from metadata if it exists, otherwise fallback to API count
    // This handles mixed scenarios where some assignments have metadata and others don't
    return count > 0 ? count : apiCount;
  };

  // Helper function to get assigned creative count for any billboard (main or package)
  const getAssignedCreativeCount = (billboardId: string, packageParentId?: string): number => {
    // If packageParentId is provided, this is a package inventory
    if (packageParentId) {
      return getContextAwareCreativeCount(billboardId, true, packageParentId);
    }

    // Otherwise, it's a standalone inventory
    return getContextAwareCreativeCount(billboardId, false);
  };

  useEffect(() => {
    // Only for multiple selection
    if (selectedBillboards.length > 1) {
      const fetchAllAssigned = async () => {
        const newMap = new Map();
        for (const bbId of selectedBillboards) {
          const billboard = getAllBillboardsById([bbId])[0];
          if (!billboard) continue;
          try {
            if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
              const mockResponse = await generateMockData(GET_ASSIGNED_CONTENT);
              let filteredCreatives = mockResponse.data.assignedContent.filter((creative: any) =>
                creative.inventoryId === billboard.inventoryId
              );
              const formattedCreatives = filteredCreatives.map((creative: any) => ({
                id: creative.id || 'unknown-id',
                name: creative.fileName || 'Unknown',
                thumbnailUrl: creative.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
                format: creative.mimeType || 'N/A',
                resolution: creative.resolution || 'N/A',
                duration: creative.duration ? String(creative.duration) : 'N/A',
                mimeType: creative.mimeType || '',
                metadata: creative.metadata || {},
                filePath: creative.filePath || '',
                status: creative.status || 'approved'
              }));
              newMap.set(billboard.id, formattedCreatives);
            } else {
              const { data } = await getAssignedContent({
                variables: {
                  accessToken: token || '',
                  billboardId: billboard.inventoryId,
                  campaignId: campaignId || '',
                  companyId
                }
              });
              if (data?.assignedContent) {
                const formattedCreatives = data.assignedContent.map((creative: any) => ({
                  id: creative.id || 'unknown-id',
                  name: creative.fileName || 'Unknown',
                  thumbnailUrl: creative.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
                  format: creative.mimeType || 'N/A',
                  resolution: creative.resolution || 'N/A',
                  duration: creative.duration ? String(creative.duration) : 'N/A',
                  mimeType: creative.mimeType || '',
                  metadata: creative.metadata || {},
                  filePath: creative.filePath || '',
                  status: creative.status || 'pending'
                }));

                // Filter creatives based on assignment context
                const isStandalone = !billboard.parentPackageId;
                const packageId = billboard.parentPackageId;

                const filteredCreatives = formattedCreatives.filter((creative: any) => {
                  const metadata = getCreativeAssignmentContext(campaignId || '', billboard.inventoryId, creative.id);

                  // If no metadata exists, treat as legacy assignment (default to standalone)
                  if (!metadata) {
                    return isStandalone;
                  }

                  // For standalone inventories: only show creatives assigned as standalone
                  if (isStandalone) {
                    return metadata.assignedAsStandalone === true;
                  }

                  // For package inventories: only show creatives assigned to this specific package
                  if (packageId) {
                    return metadata.packageId === packageId;
                  }

                  return false;
                });

                newMap.set(billboard.id, filteredCreatives);
              } else {
                newMap.set(billboard.id, []);
              }
            }
          } catch (e) {
            newMap.set(billboard.id, []);
          }
        }
        setAssignedCreatives(newMap);
      };
      fetchAllAssigned();
    }
  }, [selectedBillboards]);


  // Toggle package expansion
  const togglePackageExpansion = (billboardId: string) => {
    const isExpanding = !expandedPackages.includes(billboardId);

    setExpandedPackages(prev =>
      prev.includes(billboardId)
        ? prev.filter(id => id !== billboardId)
        : [...prev, billboardId]
    );

    // Fetch package inventories when expanding if not already fetched and not using mock data
    if (isExpanding && !packageInventories[billboardId] && import.meta.env.VITE_APP_MOCK_DATA !== 'true') {
      // Find the billboard to get its inventoryId (which is the actual packageId for packages)
      const billboard = billboards.find(b => b.id === billboardId);

      if (billboard && billboard.inventoryId) {
        getPackageInventories({
          variables: {
            accessToken: token || '',
            packageId: billboard.inventoryId, // Use the inventoryId as packageId
            page: 0,
            size: 999
          }
        });
      }
    } else if (isExpanding) {
      console.info('Package inventories already cached or using mock data for billboard:', billboardId);
    }
  };

  // Handle billboard selection
  const handleBillboardSelect = (id: string) => {
    // Single row select logic: only one billboard can be selected at a time
    if (selectedBillboards.length === 1 && selectedBillboards[0] === id) {
      // Deselect if already selected
      setSelectedBillboards([]);
      setSelectedBillboard(null);
    } else {
      // Select only the clicked billboard
      setSelectedBillboards([id]);
      const billboardObj = getAllBillboardsById([id])[0];
      setSelectedBillboard(billboardObj);
      handleBillboardClick(billboardObj);
    }
  };


  // Handle search function
  const handleSearch = async () => {
    // Validate inventory type is selected
    if (inventoryTypeFilter === 'all') {
      toast({
        title: "Selection Required",
        description: "Please select an Inventory Type before searching",
        variant: "destructive",
      });
      return;
    }

    if (!searchBillboard.trim()) {
      // toast({
      //   title: "Info",
      //   description: "Please enter a search term",
      //   variant: "default",
      // });
      return;
    }
    setSearchTerm(searchBillboard.trim());

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA !== 'true') {
        // For real API, refetch with new search term
        await refetchBillboards({
          accessToken: token || '',
          id: campaignId || '',
          searchTerm: searchBillboard.trim(),
          page: "0"
        });

        // toast({
        //   title: "Search Complete",
        //   description: `Search results for "${searchBillboard.trim()}"`,
        //   variant: "default",
        // });
      } else {
        // For mock data, the useEffect will handle filtering
        // toast({
        //   title: "Search Complete",
        //   description: `Searching for "${searchBillboard.trim()}"`,
        //   variant: "default",
        // });
      }
    } catch (error) {
      console.error('Search error:', error);
      // toast({
      //   title: "Search Error",
      //   description: "An error occurred while searching. Please try again.",
      //   variant: "destructive",
      // });
    }
  };

  // Handle reset function
  const handleResetSearch = async () => {
    setSearchBillboard('');
    setSearchTerm('');

    // Reset all filters
    setFormatFilter('all');
    setResolutionFilter('all');
    setDurationFilter('all');
    setInventoryTypeFilter('all');

    // FIX: Clear selected inventory section
    setSelectedBillboards([]);
    setSelectedBillboard(null);
    setBillboardAssignedCreatives([]);

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA !== 'true') {
        // For real API, refetch with empty search term
        await refetchBillboards({
          accessToken: token || '',
          id: campaignId || '',
          searchTerm: '',
          page: "0"
        });

        // toast({
        //   title: "Search Reset",
        //   description: "Showing all billboards",
        //   variant: "default",
        // });
      } else {
        // For mock data, reset to show all
        setFilteredBillboards(billboards);

        // toast({
        //   title: "Search Reset",
        //   description: "Showing all billboards",
        //   variant: "default",
        // });
      }
    } catch (error) {
      console.error('Reset error:', error);
      // toast({
      //   title: "Reset Error",
      //   description: "An error occurred while resetting. Please try again.",
      //   variant: "destructive",
      // });
    }
  };

  const handleClearSearchText = async () => {
    setSearchBillboard('');
    setSearchTerm('');

    // We do NOT reset format, resolution, duration, or inventoryType filters here.

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA !== 'true') {
        await refetchBillboards({
          accessToken: token || '',
          id: campaignId || '',
          searchTerm: '', // Fetch all data again (client-side filters will still apply)
          page: "0"
        });
      }
      // For mock data, the useEffect handling filters will automatically re-run 
      // because searchTerm state changed.
    } catch (error) {
      console.error('Clear search error:', error);
    }
  };
  // Helper function to get original file URL from thumbnail URL
  const getOriginalFileUrl = (thumbnailUrl: string) => {

    if (!thumbnailUrl) return thumbnailUrl;
    // Remove '_thumnail' and any file extension from the end to get the original file URL
    const originalUrl = thumbnailUrl.replace(/_thumnail\.\w+$/, '');
    return thumbnailUrl;

  };

  // Handle delete uploaded file (placeholder)
  const handleDeleteUploaded = (id: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== id));
    setSelectedCreatives(prev => prev.filter(cid => cid !== id));
  };
  // Handle file upload
  const handleFileUpload = (files: FileList | null) => {
    if (!files || files.length === 0) {
      console.warn("No files provided");
      return;
    }

    // Check if at least one billboard is selected
    if (selectedBillboards.length === 0) {
      toast({
        title: "No Billboard Selected",
        description: "Please select at least one billboard before uploading creatives",
        variant: "destructive",
      });
      return;
    }

    try {
      const newFiles = Array.from(files).map((file, index) => {
        const isVideo = file.type.startsWith('video/');
        const fileUrl = URL.createObjectURL(file);
        return {
          id: Date.now() + index, // Use timestamp to avoid ID conflicts
          name: file.name.replace(/\.[^/.]+$/, ""),
          size: file.size,
          type: file.type, // e.g. 'video/mp4' or 'image/jpeg'
          format: file.type, // use actual file type for format
          resolution: '700 X 370',
          duration: isVideo,
          thumbnail: fileUrl,
          filePath: isVideo ? fileUrl : undefined, // set filePath for video preview
          status: 'Pending'
        };
      });

      setUploadedFiles(prev => {
        const updated = [...prev, ...newFiles];
        return updated;
      });

      // Show success feedback
      if (newFiles.length > 0) {
        toast({
          title: "Upload Successful",
          description: `Successfully uploaded ${newFiles.length} file(s)! You can see them in the uploaded files table below.`,
          variant: "default",
        });
      }
    } catch (error) {
      console.error("Error processing files:", error);
      toast({
        title: "Upload Failed",
        description: "Error uploading files. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle opening assign modal for specific billboard (same as Promotional)
  const handleOpenAssignModal = (billboard?: BillboardInfo) => {
    if (!billboard && selectedBillboards.length === 0) {
      console.warn('No billboard selected for upload');
      toast({
        title: "Info",
        description: "Please select at least one billboard to assign creatives.",
        variant: "info",
      });
      return;
    }

    setAssignedCreatives(new Map());

    if (billboard) {
      setSelectedBillboard(billboard);
      setSelectedBillboards([billboard.inventoryId]); // Use inventoryId for consistency

      if (billboard.inventoryType.toLowerCase() === 'digital') {
        // For digital billboards, set resolution parameters for filtering
        setSelectedCreativesForAssign([]);
      } else {
        // For classic billboards, set panel parameters
        setSelectedCreativesForAssign([]);
      }
    } else {
      setSelectedBillboard(null);
      setSelectedCreativesForAssign([]);
    }

    setShowAssignModal(true);

    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      generateMockData(GET_ASSIGN_ECOMMERCE_ASSETS).then((mockResponse: any) => {
        let data = mockResponse.data;

        // Enhanced filtering based on billboard requirements
        if (billboard) {
          if (billboard.inventoryType.toLowerCase() === 'classic') {
            // Classic: Only images, any type and resolution allowed
            data.ecommerceAssets = data.ecommerceAssets.filter((asset: any) => {
              const isImage = asset.mimeType?.toLowerCase().includes('image');
              return isImage;
            });
          } else {
            // Digital: Images and videos with format, resolution, and duration matching
            const supportedImageFormats = billboard.inventoryDetails?.imageSupport || ['image/jpeg', 'image/png'];
            const supportedVideoFormats = billboard.inventoryDetails?.videoSupport || ['video/mp4'];
            const requiredResolution = `${billboard.inventoryDetails?.resolution1Width}x${billboard.inventoryDetails?.resolution1Height}`;
            const requiredDuration = parseFloat(billboard.duration) || 15;

            data.ecommerceAssets = data.ecommerceAssets.filter((asset: any) => {
              const isImage = asset.mimeType?.toLowerCase().includes('image');
              const isVideo = asset.mimeType?.toLowerCase().includes('video');

              if (isImage) {
                const isFormatSupported = supportedImageFormats.some(supportedFormat =>
                  asset.mimeType?.toLowerCase().includes(supportedFormat.toLowerCase())
                );
                const normalizedAssetResolution = (asset.resolution || '').toLowerCase().replace(/\s+/g, '').replace('x', 'x');
                const normalizedRequiredResolution = requiredResolution.toLowerCase().replace(/\s+/g, '');
                const isResolutionMatch = normalizedAssetResolution === normalizedRequiredResolution;
                return isFormatSupported && isResolutionMatch;
              }

              if (isVideo) {
                const isFormatSupported = supportedVideoFormats.some(supportedFormat =>
                  asset.mimeType?.toLowerCase().includes(supportedFormat.toLowerCase())
                );
                const normalizedAssetResolution = (asset.resolution || '').toLowerCase().replace(/\s+/g, '').replace('x', 'x');
                const normalizedRequiredResolution = requiredResolution.toLowerCase().replace(/\s+/g, '');
                const isResolutionMatch = normalizedAssetResolution === normalizedRequiredResolution;
                const assetDuration = parseFloat(asset.duration) || 0;
                const isDurationMatch = Math.abs(assetDuration - requiredDuration) <= 0.5; // Updated tolerance to ±0.5
                return isFormatSupported && isResolutionMatch && isDurationMatch;
              }

              return false;
            });
          }
        }

        const formattedCreatives = data.ecommerceAssets.map((asset: any) => ({
          id: asset.id || 'unknown-id',
          name: asset.fileName || 'Unknown',
          thumbnailUrl: asset.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
          format: asset.mimeType || 'N/A',
          resolution: asset.resolution || 'N/A',
          duration: asset.duration ? String(asset.duration) : 'N/A',
          mimeType: asset.mimeType || '',
          filePath: asset.filePath || '',
          metadata: asset.metadata || {}
        }));

        const uniqueCreatives = Array.from(
          new Map(formattedCreatives.map((creative: CreativeItem) => [creative.id, creative])).values()
        ) as CreativeItem[];

        setCreatives(uniqueCreatives);
        setPopupTotalPages(Math.ceil(data.ecommerceAssets[0]?.totalPages || 1));
      });
    } else {
      // Format the resolution for API call
      const formattedResolution = billboard?.resolution && billboard.resolution !== 'N/A'
        ? billboard.resolution.replace(/X/g, 'x').replace(/\s+/g, '')
        : '';

      // Call refetch with explicit variables
      refetchEcommerceAssets({
        accessToken: token || '',
        companyId,
        userId: userId || '',
        page: 0, // Reset to first page when opening modal
        resolution: formattedResolution,
        size: 50,
        searchTerm: ''
      });
    }
  };

  // Handle viewing assigned creatives for a billboard (triggered on billboard click)
  const handleBillboardClick = async (billboard: BillboardInfo) => {
    setSelectedBillboard(billboard);
    setSelectedBillboards([billboard.id]);

    if (assignedCreatives.has(billboard.id)) {
      const cachedCreatives = assignedCreatives.get(billboard.id) || [];
      setBillboardAssignedCreatives(cachedCreatives);
      // Also update the filter state so the list appears immediately
      setFilteredBillboardAssignedCreatives(cachedCreatives);
      return;
    }

    setIsLoading(true);

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        // Generate mock assigned creatives - ensure some creatives are assigned to this billboard
        generateMockData(GET_ASSIGNED_CONTENT).then((mockResponse: any) => {
          let assignedCreatives = mockResponse.data.assignedContent || [];

          // If no creatives are assigned to this billboard, create some mock ones
          let filteredCreatives = assignedCreatives.filter((creative: any) =>
            creative.inventoryId === billboard.inventoryId
          );

          if (filteredCreatives.length === 0) {
            // Create 2-3 mock assigned creatives for this billboard with different statuses
            filteredCreatives = [
              {
                id: `assigned-${billboard.inventoryId}-1`,
                fileName: 'Approved Creative.mp4',
                thumbnail: `https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb_thumnail.mp4`,
                mimeType: 'video/mp4',
                resolution: billboard.resolution || '1920x1080',
                duration: 30,
                inventoryId: billboard.inventoryId,
                status: 'approved',
                comments: null
              },
              {
                id: `assigned-${billboard.inventoryId}-2`,
                fileName: 'Rejected Creative.jpg',
                thumbnail: `https://via.placeholder.com/1920x1080/FF6B6B/FFFFFF?text=Rejected+Creative_thumnail.jpg`,
                mimeType: 'image/jpeg',
                resolution: billboard.resolution || '1920x1080',
                duration: null,
                inventoryId: billboard.inventoryId,
                status: 'rejected',
                comments: 'Low image quality and does not meet brand guidelines. Please use higher resolution images with proper brand colors.'
              },
              {
                id: `assigned-${billboard.inventoryId}-3`,
                fileName: 'Pending Creative.mp4',
                thumbnail: `https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny_thumnail.mp4`,
                mimeType: 'video/mp4',
                resolution: billboard.resolution || '1920x1080',
                duration: 15,
                inventoryId: billboard.inventoryId,
                status: 'pending',
                comments: null
              }
            ];
          }

          setBillboardAssignedCreatives(filteredCreatives.map((creative: any) => ({
            id: creative.id || 'unknown-id',
            name: creative.fileName || 'Unknown',
            thumbnailUrl: creative.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
            format: creative.mimeType || 'N/A',
            resolution: creative.resolution || 'N/A',
            duration: creative.duration ? String(creative.duration) : 'N/A',
            mimeType: creative.mimeType || '',
            metadata: creative.metadata || {},
            status: creative.status || 'approved',
            comments: creative.comments || null
          })));
        });
      } else {
        // Use real API - get assigned content for this billboard
        const { data } = await getAssignedContent({
          variables: {
            accessToken: token || '',
            billboardId: billboard.inventoryId,
            campaignId: campaignId || '',
            companyId
          }
        });

        if (data?.assignedContent) {

          const formattedCreatives = data.assignedContent.map((creative: any) => {
            return {
              id: creative.id || 'unknown-id',
              name: creative.fileName || 'Unknown',
              thumbnailUrl: creative.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
              format: creative.mimeType || 'N/A',
              resolution: creative.resolution || 'N/A',
              duration: creative.duration ? String(creative.duration) : 'N/A',
              mimeType: creative.mimeType || '',
              metadata: creative.metadata || {},
              status: creative.status || 'pending',
              comments: creative.comments || creative.rejectionReason || creative.rejectionComments || creative.reviewComments || null
            };
          });

          // Filter creatives based on assignment context
          const isStandalone = !billboard.parentPackageId;
          const packageId = billboard.parentPackageId;

          const filteredCreatives = formattedCreatives.filter((creative: CreativeItem) => {
            const metadata = getCreativeAssignmentContext(campaignId || '', billboard.inventoryId, creative.id);

            // If no metadata exists, treat as legacy assignment (default to standalone)
            if (!metadata) {
              return isStandalone;
            }

            // For standalone inventories: only show creatives assigned as standalone
            if (isStandalone) {
              return metadata.assignedAsStandalone === true;
            }

            // For package inventories: only show creatives assigned to this specific package
            if (packageId) {
              return metadata.packageId === packageId;
            }

            return false;
          });

          setAssignedCreatives(prev => {
            const newMap = new Map(prev);
            newMap.set(billboard.id, filteredCreatives);
            return newMap;
          });

          setBillboardAssignedCreatives(filteredCreatives);
        } else {
          setBillboardAssignedCreatives([]);
          setAssignedCreatives(prev => new Map(prev).set(billboard.id, []));
        }
      }
    } catch (error) {
      console.error('Error fetching assigned creatives:', error);
      toast({
        title: "Error",
        description: "Failed to load assigned creatives. Please try again.",
        variant: "destructive",
      });
      setBillboardAssignedCreatives([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter creatives based on billboard compatibility
  const getCompatibleCreatives = (billboard: BillboardInfo) => {
    // If we have no creatives, return empty array
    if (creatives.length === 0) {
      console.warn('❌ No creatives available');
      return [];
    }

    const filtered = creatives.filter(creative => {
      const creativeFormat = creative.format?.toLowerCase() || '';
      const creativeMimeType = creative.mimeType?.toLowerCase() || '';
      const creativeResolution = creative.resolution || '';
      const creativeDuration = parseFloat(creative.duration) || 0;

      // For Classic Billboards: Only images, any type and resolution allowed
      if (billboard.inventoryType.toLowerCase() === 'classic') {
        // Check if creative is an image (any type and resolution)
        const isImage = creativeFormat.includes('image') || creativeMimeType.includes('image') ||
          creativeFormat.includes('jpeg') || creativeFormat.includes('jpg') ||
          creativeFormat.includes('png') || creativeFormat.includes('gif') ||
          creativeFormat.includes('bmp') || creativeFormat.includes('webp') ||
          creativeFormat.includes('tiff') || creativeFormat.includes('svg');
        if (!isImage) {
          console.warn(`❌ Classic - ${creative.name} is not an image`);
          return false;
        }
        return true;
      }

      // For Digital Billboards
      else {
        const supportedImageFormats = billboard.inventoryDetails?.imageSupport || [];
        const supportedVideoFormats = billboard.inventoryDetails?.videoSupport || [];
        const requiredResolution = `${billboard.inventoryDetails?.resolution1Width}x${billboard.inventoryDetails?.resolution1Height}`;
        const requiredDuration = parseFloat(billboard.duration) || 0;

        // Check if creative is image or video
        const isImage = creativeFormat.includes('image') || creativeMimeType.includes('image') ||
          creativeFormat.includes('jpeg') || creativeFormat.includes('jpg') || creativeFormat.includes('png');

        const isVideo = creativeFormat.includes('video') || creativeMimeType.includes('video') ||
          creativeFormat.includes('mp4') || creativeFormat.includes('mov') ||
          creativeFormat.includes('avi') || creativeFormat.includes('webm');

        if (!isImage && !isVideo) {
          console.warn(`❌ Digital - ${creative.name} is neither image nor video`);
          return false;
        }

        // For Images (Digital)
        if (isImage) {
          // Check format support with JPEG/JPG equivalence
          const isFormatSupported = supportedImageFormats.some(supportedFormat => {
            const supportedLower = supportedFormat.toLowerCase();
            
            // Handle JPEG/JPG equivalence
            if (supportedLower.includes('jpeg') || supportedLower.includes('jpg')) {
              return creativeMimeType.includes('jpeg') || creativeMimeType.includes('jpg') ||
                     creativeFormat.includes('jpeg') || creativeFormat.includes('jpg');
            }
            
            // Handle other formats normally
            return creativeMimeType.includes(supportedLower) ||
                   creativeFormat.includes(supportedLower);
          });

          if (!isFormatSupported) {
            console.warn(`❌ Digital Image - ${creative.name} format not supported`, {
              supportedFormats: supportedImageFormats,
              creativeMimeType,
              creativeFormat
            });
            return false;
          }

          // Check resolution match (exact)
          const normalizedCreativeResolution = creativeResolution.toLowerCase().replace(/\s+/g, '').replace('x', 'x');
          const normalizedRequiredResolution = requiredResolution.toLowerCase().replace(/\s+/g, '');
          const isResolutionMatch = normalizedCreativeResolution === normalizedRequiredResolution;
          return isResolutionMatch;
        }

        // For Videos (Digital)
        if (isVideo) {
          // Check format support
          const isFormatSupported = supportedVideoFormats.some(supportedFormat =>
            creativeMimeType.includes(supportedFormat.toLowerCase()) ||
            creativeFormat.includes(supportedFormat.toLowerCase())
          );

          if (!isFormatSupported) {
            console.warn(`❌ Digital Video - ${creative.name} format not supported`, {
              supportedFormats: supportedVideoFormats,
              creativeMimeType,
              creativeFormat
            });
            return false;
          }

          // Check resolution match (exact)
          const normalizedCreativeResolution = creativeResolution.toLowerCase().replace(/\s+/g, '').replace('x', 'x');
          const normalizedRequiredResolution = requiredResolution.toLowerCase().replace(/\s+/g, '');
          const isResolutionMatch = normalizedCreativeResolution === normalizedRequiredResolution;

          if (!isResolutionMatch) {
            console.warn(`❌ Digital Video - ${creative.name} resolution mismatch`, {
              required: requiredResolution,
              creative: creativeResolution
            });
            return false;
          }

          // Check duration match (tolerance: ±0.5 seconds)
          const durationTolerance = 0.5;

          // Skip duration check if billboard duration is not valid
          if (requiredDuration <= 0 || billboard.duration === 'N/A') {
            console.warn(`⚠️ Digital Video - ${creative.name} - billboard has no valid duration, skipping duration check`);
            return true; // Accept video if other checks pass
          }

          const isDurationMatch = Math.abs(creativeDuration - requiredDuration) <= durationTolerance;
          return isDurationMatch;
        }
      }

      return false;
    });

    return filtered;
  };

  // Apply search filter to popup creatives
  useEffect(() => {
    if (selectedBillboard) {
      // Always apply compatibility filtering first
      const compatibleCreatives = getCompatibleCreatives(selectedBillboard);

      if (popupSearchTerm.trim()) {
        // Then apply search filter if there's a search term
        const filtered = compatibleCreatives.filter(creative =>
          creative.name.toLowerCase().includes(popupSearchTerm.toLowerCase())
        );
        setFilteredPopupCreatives(filtered);
      } else {
        // If no search term, show all compatible creatives
        setFilteredPopupCreatives(compatibleCreatives);
      }
    } else {
      setFilteredPopupCreatives(null);
    }
  }, [popupSearchTerm, selectedBillboard, creatives]);

  // Apply search filter to assigned creatives
  useEffect(() => {
    if (searchCreativeTerm.trim()) {
      const filtered = billboardAssignedCreatives.filter(creative =>
        creative.name.toLowerCase().includes(searchCreativeTerm.toLowerCase()) ||
        creative.format.toLowerCase().includes(searchCreativeTerm.toLowerCase()) ||
        creative.resolution.toLowerCase().includes(searchCreativeTerm.toLowerCase()) ||
        (creative.status && creative.status.toLowerCase().includes(searchCreativeTerm.toLowerCase()))
      );
      setFilteredBillboardAssignedCreatives(filtered);
    } else {
      setFilteredBillboardAssignedCreatives(billboardAssignedCreatives);
    }
  }, [searchCreativeTerm, billboardAssignedCreatives]);

  // Helper to check if all selected billboards match
  const isMultiSelectValid = (() => {
    if (selectedBillboards.length < 2) return false;
    const selectedObjs = getAllBillboardsById(selectedBillboards);
    if (selectedObjs.length < 2) return false;
    const first = selectedObjs[0];
    return selectedObjs.every(b =>
      b.format === first.format &&
      b.resolution === first.resolution &&
      b.duration === first.duration &&
      b.inventoryType === first.inventoryType
    );
  })();

  // Handle assign creative mutation

  const handleAssignCreative = async () => {
    if (selectedBillboards.length === 0 || selectedCreatives.length === 0) {
      toast({
        title: "Error",
        description: "Please select both billboards and creatives to assign",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);

      // Build the inputs array for the mutation
      const inputs = selectedBillboards.flatMap(billboardId =>
        selectedCreatives.map(creativeId => {
          const billboardObj = getAllBillboardsById([billboardId])[0];
          const creativeObj = creatives.find(c => c.id === creativeId);

          // Fallback to find billboard in the billboards array if getAllBillboardsById doesn't work
          let finalBillboardObj: BillboardInfo | undefined = billboardObj;
          if (!billboardObj || !billboardObj.inventoryType) {
            finalBillboardObj = billboards.find(b => b.inventoryId === billboardId);
          }

          // Additional fallback to selectedBillboard if it matches
          if ((!finalBillboardObj || !finalBillboardObj.inventoryType) && selectedBillboard && selectedBillboard.inventoryId === billboardId) {
            finalBillboardObj = selectedBillboard;
          }

          // Ensure we have a valid billboard object
          if (!finalBillboardObj) {
            return null; // Skip this assignment
          }

          const inventoryType = finalBillboardObj.inventoryType || '';

          return {
            accessToken: token || '',
            assetId: creativeId,
            billboardId: finalBillboardObj.inventoryId || billboardId,
            campaignId: campaign?.id || '',
            dealId: finalBillboardObj.dealId || campaign?.dealId || '',
            inventoryType: inventoryType,
            landingUrl: '',
            mediaOwnerId: creativeObj?.metadata?.mediaOwnerId || companyId,
            mobileInventory: false,
            playInSequence: true,
            skippable: true,
            schedule: [
              {
                scheduleSequence: 1,
                startDate: billboardObj?.startDate
                  ? format(setHours(setMinutes(new Date(billboardObj.startDate), 30), 6), "yyyy-MM-dd'T'HH:mm:ss")
                  : format(setHours(setMinutes(new Date(), 30), 6), "yyyy-MM-dd'T'HH:mm:ss"),
                endDate: billboardObj?.endDate
                  ? format(setHours(setMinutes(new Date(billboardObj.endDate), 30), 6), "yyyy-MM-dd'T'HH:mm:ss")
                  : format(setHours(setMinutes(new Date(Date.now() + 15 * 24 * 60 * 60 * 1000), 30), 6), "yyyy-MM-dd'T'HH:mm:ss"),
                time: [
                  {
                    startTime: '06:00',
                    endTime: '21:00',
                    executionDays: []
                  }
                ],
                content: [
                  {
                    contentSequence: 1,
                    status: true,
                    rules: ""
                  }
                ]
              }
            ]
          };
        })
      ).filter(input => input !== null);

      const response = await assignAssetToCampaign({
        variables: {
          inputs
        }
      });

      // Check for errors in the response
      if (response.errors && response.errors.length > 0) {
        const errorMsg = response.errors[0]?.message || "Failed to assign creatives. Please try again.";
        toast({
          title: "Error",
          description: errorMsg,
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Check for API-level error (null data)
      if (!response.data?.assignAssetToCampaign) {
        toast({
          title: "Error",
          description: (response?.errors && response.errors.length > 0 ? response.errors[0]?.message : "Failed to assign creatives. Please try again."),
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Success
      toast({
        title: "Success",
        description: response.data.assignAssetToCampaign.message || `Successfully assigned ${selectedCreatives.length} creative(s) to ${selectedBillboards.length} billboard(s)`,
        variant: "default",
      });

      setSelectedCreatives([]);
      setSelectedBillboards([]);
      setShowAssignModal(false);
      setSuccessMessage(response.data.assignAssetToCampaign.message || "Successfully assigned creatives to billboards");
      setShowSuccessModal(true);

      // Refresh assigned creatives for each selected billboard
      selectedBillboards.forEach(billboardId => {
        const billboardObj = getAllBillboardsById([billboardId])[0];
        if (billboardObj) {
          handleBillboardClick(billboardObj);
        }
      });

    } catch (error: any) {

      let errorMessage = "Failed to assign creatives. Please try again.";

      // 1. Check for API error messages buried in networkError (HTTP 400 Bad Request)
      // We cast to 'any' to avoid TypeScript errors regarding 'result' property
      if (error.networkError && (error.networkError as any).result) {
        const result = (error.networkError as any).result;
        if (result.errors && result.errors.length > 0) {
          errorMessage = result.errors[0].message;
        }
      }
      // 2. Check for standard GraphQL errors
      else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      }
      // 3. Fallback to the top-level error message if it's not the generic "Response not successful"
      else if (error.message && !error.message.includes("Response not successful")) {
        errorMessage = error.message;
      }

      toast({
        title: "Assignment Failed",
        description: errorMessage, // This will now display "The creative is already assigned..."
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };


  // Handle removing assigned creative from billboard
  const handleRemoveCreative = (_billboardId: string, creativeId: string) => {
    setDeleteItemId(creativeId);
    setShowDeleteModal(true);
  };

  // Handle delete creative - this is the confirmDelete function
  const confirmDelete = async () => {
    if (!deleteItemId) {
      console.error('Missing deleteItemId for deletion');
      return;
    }

    setIsLoading(true);
    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        // Remove from current view list
        setBillboardAssignedCreatives(prev =>
          prev.filter(creative => creative.id !== deleteItemId)
        );

        // Remove from cache (Map)
        setAssignedCreatives(prev => {
          const newMap = new Map(prev);
          newMap.forEach((creatives, key) => {
            const updatedCreatives = creatives.filter(c => c.id !== deleteItemId);
            newMap.set(key, updatedCreatives);
          });
          return newMap;
        });

        toast({
          title: "Success",
          description: "Creative deleted successfully",
          variant: "default",
        });

        // Update the count on the left sidebar
        setApprovedCreativeCount(prev => {
          return prev.map(countItem => {
            if (selectedBillboard && countItem.id === selectedBillboard.inventoryId) {
              return {
                ...countItem,
                count: Math.max(0, countItem.count - 1)
              };
            }
            return countItem;
          });
        });
      }
      else {
        if (!token) {
          console.error('Missing access token for deletion');
          toast({
            title: "Error",
            description: "Authentication required",
            variant: "destructive",
          });
          return;
        }

        const response = await deleteContent({
          variables: {
            accessToken: token,
            contentIds: [deleteItemId]
          }
        });

        // Error Handling
        if (response.errors && response.errors.length > 0) {
          const errorMsg = response.errors[0].message || "Unknown error";
          toast({
            title: "Error",
            description: errorMsg,
            variant: "destructive",
          });
          return;
        }

        const message = response.data?.deleteContent?.[0]?.message;

        if (message && message.startsWith('error.')) {
          toast({
            title: "Error",
            description: "Internal server error",
            variant: "destructive",
          });
        } else {
          // SUCCESS!
          toast({
            title: "Success",
            description: message || "Creative deleted successfully",
            variant: "default",
          });

          // Remove assignment metadata from localStorage
          if (selectedBillboard) {
            deleteAssignmentMetadata(campaignId || '', selectedBillboard.inventoryId, deleteItemId);
          }

          // FIX A: Update the Right Sidebar List immediately (Visual Fix)
          setBillboardAssignedCreatives(prev =>
            prev.filter(creative => creative.id !== deleteItemId)
          );

          // FIX B: Update the Global Cache (Memory Fix)
          // This prevents the creative from reappearing if you click away and come back
          setAssignedCreatives(prev => {
            const newMap = new Map(prev);
            newMap.forEach((creatives, key) => {
              const updatedCreatives = creatives.filter(c => c.id !== deleteItemId);
              newMap.set(key, updatedCreatives);
            });
            return newMap;
          });

          // FIX C: Update the "X Creatives assigned" Count (Sidebar Fix)
          setApprovedCreativeCount(prev => {
            return prev.map(countItem => {
              if (selectedBillboard && countItem.id === selectedBillboard.inventoryId) {
                return {
                  ...countItem,
                  count: Math.max(0, countItem.count - 1) // Reduce count by 1 instantly
                };
              }
              return countItem;
            });
          });
        }
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Error",
        description: "Failed to delete creative. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
      setDeleteItemId(null);
    }
  };

  // Handle creative click to view in modal
  const handleCreativeClick = (creative: CreativeItem) => {

    // Log for debugging video preview issues
    if (creative.mimeType?.startsWith('video/')) {
      if (!creative.filePath) {
        console.warn('Video creative is missing filePath. Video cannot be played.');
      } else {
        console.info('Video preview will use filePath:', creative.filePath);
      }
      // If you have persistent issues with video playback, consider using a dedicated video player library like react-player for more formats/support.
    }

    // Don't show preview modal if creative has no actual media (using nomedia placeholder)
    const isNoMedia = creative.thumbnailUrl.includes('/nomedia.jpg') ||
      creative.thumbnailUrl.includes('/public/nomedia.jpg') ||
      !creative.thumbnailUrl ||
      creative.thumbnailUrl === 'null';

    if (isNoMedia) {
      // Show a toast message instead of opening the modal
      toast({
        title: "Info",
        description: "This creative doesn't have media content to preview.",
        variant: "default",
      });
      return;
    }

    setSelectedCreativeForView(creative);
    // Set rejection reason if creative is rejected
    if (creative.status?.toLowerCase() === 'rejected') {
      setSelectedRejectionReason(creative.comments || '');
    }
    setShowCreativeModal(true);
  };

  // Handle delete creative - old function kept for backwards compatibility
  const handleDeleteCreative = async (creativeId: string) => {
    try {
      setIsLoading(true);

      await deleteContent({
        variables: {
          id: creativeId,
          userId: userId,
          companyId: companyId,
        }
      });

      toast({
        title: "Success",
        description: "Creative deleted successfully",
        variant: "default",
      });

      // Refresh creatives list
      setCreatives(prev => prev.filter(creative => creative.id !== creativeId));

    } catch (error) {
      console.error('Error deleting creative:', error);
      toast({
        title: "Error",
        description: "Failed to delete creative. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
      setDeleteItemId(null);
    }
  };

  // Pagination functions
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePopupPreviousPage = () => {
    if (popupCurrentPage > 1) {
      setPopupCurrentPage(popupCurrentPage - 1);
    }
  };

  const handlePopupNextPage = () => {
    if (popupCurrentPage < popupTotalPages) {
      setPopupCurrentPage(popupCurrentPage + 1);
    }
  };

  // Pagination helper functions
  const getPaginatedUploadedFiles = () => {
    const startIndex = (uploadedFilesCurrentPage - 1) * uploadedFilesPerPage;
    const endIndex = startIndex + uploadedFilesPerPage;
    return uploadedFiles.slice(startIndex, endIndex);
  };

  const uploadedFilesTotalPages = Math.ceil(uploadedFiles.length / uploadedFilesPerPage);

  const getPaginatedAssignCreatives = (creativesToShow: any[]) => {
    const startIndex = (assignCreativesCurrentPage - 1) * assignCreativesPerPage;
    const endIndex = startIndex + assignCreativesPerPage;
    return creativesToShow.slice(startIndex, endIndex);
  };

  const getAssignCreativesTotalPages = (creativesToShow: any[]) => {
    return Math.ceil(creativesToShow.length / assignCreativesPerPage);
  };

  // Handle drag and drop
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      // Only set dragActive to false if we're leaving the entire drop zone
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;

      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setDragActive(false);
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    // Check for files from file system
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
    // Check for creative data from content hub
    else {
      const creativeData = e.dataTransfer.getData("application/json");
      if (creativeData) {
        try {
          const creative = JSON.parse(creativeData);
          handleContentHubDrop(creative);
        } catch (error) {
          console.error("Error parsing dropped creative data:", error);
        }
      } else {
        console.info("No files or creative data found in drop event");
      }
    }
  };

  // Handle dropping creative from content hub
  const handleContentHubDrop = (creative: any) => {
    if (selectedBillboards.length === 0) {
      toast({
        title: "No Billboard Selected",
        description: "Please select at least one billboard before adding creatives from content hub",
        variant: "destructive",
      });
      return;
    }

    // Use assetId from API creative object
    if (!creative || !creative.id) {
      toast({
        title: "Error",
        description: "Invalid creative dropped. Please try again.",
        variant: "destructive",
      });
      return;
    }

    // Optionally add to uploadedFiles for UI, but use API id
    const isVideo = (creative.mimeType || creative.format || '').toLowerCase().includes('video');
    const newFile = {
      id: creative.id, // Use assetId from API
      name: creative.name,
      size: 0, // Content hub items don't have file sizes
      type: isVideo ? (creative.mimeType || 'video/mp4') : (creative.mimeType || 'image/jpeg'),
      format: creative.format || (isVideo ? 'video/mp4' : 'image/jpeg'),
      resolution: creative.resolution,
      duration: creative.duration || '',
      thumbnail: creative.thumbnailUrl,
      filePath: isVideo ? creative.filePath : undefined, // set filePath for video preview
      status: 'From Content Hub'
    };
    setUploadedFiles(prev => {
      // Avoid duplicates
      if (prev.some(file => file.id === creative.id)) return prev;
      return [...prev, newFile];
    });

    // Update selectedCreativesForAssign with assetId from API
    setSelectedCreativesForAssign(prev => {
      if (prev.includes(creative.id)) return prev;
      return [...prev, creative.id];
    });

    // Also update selectedCreatives state for legacy/other flows
    setSelectedCreatives(prev => {
      if (prev.includes(creative.id)) return prev;
      return [...prev, creative.id];
    });

    toast({
      title: "Creative Added",
      description: `Added "${creative.name}" from content hub successfully`,
      variant: "default",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-[95vw] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-emerald-600">Assign creative</h1>
            <Button
              variant="outline"
              onClick={() => navigate("/my-campaigns")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
          </div>
          <p className="text-gray-600 mb-4">Link creative assets to specific inventories and Packages</p>
          <div className="flex flex-wrap gap-6 text-sm mt-3">
            <div>
              <span className="text-gray-600">Campaign name:</span>
              <span className="ml-2 font-medium">{campaign?.name || 'Loading...'}</span>
            </div>
            <div>
              <span className="text-gray-600">Deal ID:</span>
              <span className="ml-2 font-medium">{campaign?.dealId || 'Loading...'}</span>
            </div>
            <div>
              <span className="text-gray-600">Duration:</span>
              <span className="ml-2 font-medium">
                {(() => {
                  const start = campaign?.startDate;
                  const end = campaign?.endDate;
                  if (!start || !end) return 'Loading...';
                  const startDate = new Date(start);
                  const endDate = new Date(end);

                  const formatDate = (date: Date): string => {
                    const day = date.getDate().toString().padStart(2, '0');
                    const month = date.toLocaleDateString('en-GB', { month: 'short' }).replace('Sept', 'Sep');
                    const year = date.getFullYear();
                    return `${day} ${month} ${year}`;
                  };

                  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
                })()}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Campaign Status:</span>
              <span className="ml-2 font-medium">{campaign?.status || 'Loading...'}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-8">
          {/* Left Column - Billboards */}
          <div className="xl:col-span-2">
            <Card className="border border-gray-200 shadow-sm mb-6">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="flex items-center gap-4">
                    <h2 className="text-lg sm:text-xl font-semibold text-emerald-600">Inventories</h2>
                    <div className="flex items-center gap-4">
                      {/* Inventory Type Filter with Tooltip */}
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div>
                              <Select
                                value={inventoryTypeFilter}
                                onValueChange={(value) => {
                                  setInventoryTypeFilter(value);

                                  // FIX: Clear search input and search term when inventory type changes
                                  setSearchBillboard('');
                                  setSearchTerm('');
                                  // FIX: Clear selected inventory section when type changes
                                  setSelectedBillboards([]);
                                  setSelectedBillboard(null);
                                  setBillboardAssignedCreatives([]);

                                  // Ensure we fetch fresh data without the search constraint (for real API)
                                  if (import.meta.env.VITE_APP_MOCK_DATA !== 'true') {
                                    refetchBillboards({
                                      accessToken: token || '',
                                      id: campaignId || '',
                                      searchTerm: '',
                                      page: "0"
                                    });
                                  }
                                }}
                              >
                                <SelectTrigger className="w-48 focus:ring-emerald-500 focus:border-emerald-500">
                                  <SelectValue placeholder="Inventory Type" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                  <SelectItem value="all">All Types</SelectItem>
                                  {inventoryTypes.filter(type => type && type !== "").map((type) => {
                                    const displayType = _inventoryTypeDisplayMapping[type.toLowerCase()] || type;
                                    return (
                                      <SelectItem key={type} value={type.toLowerCase()}>
                                        {displayType.charAt(0).toUpperCase() + displayType.slice(1).toLowerCase()}
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Filter by Digital, Classic, or Package type</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      {/* Search Input with Tooltip */}
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="relative">
                              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                              <Input
                                placeholder={inventoryTypeFilter === 'all' ? "Select type first..." : "Search Inventories"}
                                value={searchBillboard}
                                onChange={(e) => setSearchBillboard(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSearch();
                                  }
                                }}
                                disabled={inventoryTypeFilter === 'all'}
                                className="pl-10 pr-10 w-80 px-5 focus:ring-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed"
                              />
                              {(searchTerm || searchBillboard) && (
                                <button
                                  onClick={handleClearSearchText}
                                  disabled={billboardsLoading}
                                  className="absolute right-3 top-3 text-gray-400 hover:text-emerald-600 transition-colors"
                                  title="Clear search"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">
                              {inventoryTypeFilter === 'all'
                                ? 'Please select an Inventory Type first to search'
                                : 'Search by inventory name, ID, or Deal ID'}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleResetSearch}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2 py-1"
                      >
                        Reset Filter
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">Filter by:</span>
                  <Select value={formatFilter} onValueChange={setFormatFilter}>
                    <SelectTrigger className="w-40 focus focus:ring-emerald-500 focus:border-emerald-500">
                      <SelectValue placeholder="Format" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Formats</SelectItem>
                      {formats.filter(format => format && format.format && format.format !== "").map((format) => (
                        <SelectItem key={`${format.type}-${format.format}`} value={format.format}>
                          {format.format} ({format.type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={resolutionFilter} onValueChange={setResolutionFilter}>
                    <SelectTrigger className="w-48 focus:ring-emerald-500 focus:border-emerald-500">
                      <SelectValue placeholder="Resolution" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Resolutions</SelectItem>
                      {resolutions.filter(resolution => resolution && resolution !== "").map((resolution) => (
                        <SelectItem key={resolution} value={resolution}>
                          {resolution}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={durationFilter} onValueChange={setDurationFilter}>
                    <SelectTrigger className="w-40 focus:ring-emerald-500 focus:border-emerald-500">
                      <SelectValue placeholder="Duration" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Durations</SelectItem>
                      {durations.filter(duration => duration && duration !== "").map((duration) => (
                        <SelectItem key={duration} value={duration}>
                          {duration}s
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Billboards Table with Inventory Type Segregation */}
            <div className="space-y-6">
              {filteredBillboards.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">
                    {inventoryTypeFilter === 'package'
                      ? 'No Packages match the selected filters.'
                      : inventoryTypeFilter !== 'all'
                        ? (() => {
                          // Use the mapping object to get the friendly name (e.g. "static_billboard" -> "Classic")
                          const displayType = _inventoryTypeDisplayMapping[inventoryTypeFilter.toLowerCase()] || inventoryTypeFilter;
                          // Capitalize the first letter just in case
                          const formattedType = displayType.charAt(0).toUpperCase() + displayType.slice(1);
                          return `No ${formattedType} Inventories match the selected filters.`;
                        })()
                        : 'No Inventories match the selected filters.'}
                  </p>
                </div>
              ) : (() => {
                const groupedBillboards = filteredBillboards.reduce((acc, billboard) => {
                  const inventoryType = billboard.inventoryType || 'Unknown';
                  if (!acc[inventoryType]) acc[inventoryType] = [];
                  acc[inventoryType].push(billboard);
                  return acc;
                }, {} as Record<string, typeof filteredBillboards>);

                const getTypeColor = (inventoryType: string) => {
                  if (isDigitalInventory(inventoryType)) return 'border-l-blue-500 bg-blue-50';
                  if (isClassicInventory(inventoryType)) return 'border-l-green-500 bg-green-50';

                  switch (inventoryType.toLowerCase()) {
                    case 'package': return 'border-l-purple-500 bg-purple-50';
                    case 'transit': return 'border-l-orange-500 bg-orange-50';
                    default: return 'border-l-gray-500 bg-gray-50';
                  }
                };

                const sortedInventoryTypes = Object.entries(groupedBillboards).sort(([a], [b]) => {
                  const order = {
                    'Digital': 0,
                    'digital': 0,
                    'Classic': 1,
                    'classic': 1,
                    'Package': 2,
                    'package': 2,
                    'Transit': 3,
                    'transit': 3
                  };
                  return (order[a as keyof typeof order] ?? 99) - (order[b as keyof typeof order] ?? 99);
                });

                // Filter out empty groups
                const filteredSortedInventoryTypes = sortedInventoryTypes.filter(([_, billboards]) => billboards.length > 0);

                return filteredSortedInventoryTypes.map(([inventoryType, billboards]) => (
                  <div key={inventoryType} className="mb-8">
                    <div className={`border-l-4 ${getTypeColor(inventoryType)} px-6 py-3 mb-4 rounded-r-md shadow-sm bg-gradient-to-r from-transparent to-gray-50`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${inventoryType.toLowerCase().includes('digital') ? 'bg-blue-500' :
                          inventoryType.toLowerCase().includes('classic') || inventoryType.toLowerCase().includes('static') ? 'bg-green-500' :
                            inventoryType.toLowerCase() === 'package' ? 'bg-purple-500' :
                              inventoryType.toLowerCase() === 'transit' ? 'bg-orange-500' :
                                'bg-gray-500'
                          }`}></div>
                        <h3 className="font-bold text-lg text-emerald-600 ">
                          {inventoryType.toLowerCase() === 'package'
                            ? 'Packages'
                            : _inventoryTypeDisplayMapping[inventoryType.toLowerCase()] || inventoryType.charAt(0).toUpperCase() + inventoryType.slice(1).toLowerCase()
                          } ({billboards.length} {
                            inventoryType.toLowerCase() === 'package'
                              ? (billboards.length === 1 ? 'Package' : 'Packages')
                              : (billboards.length === 1 ? 'Inventory' : 'Inventories')
                          })
                        </h3>
                      </div>
                    </div>
                    <Card>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                {/* <TableHead className="w-12"></TableHead> */}
                                <TableHead className="min-w-48">Inventory Name</TableHead>
                                <TableHead className="min-w-24">Format</TableHead>
                                <TableHead className="min-w-24">
                                  {/* FIX: Use helper function */}
                                  {isClassicInventory(inventoryType) ? 'Size' : 'Resolution'}
                                </TableHead>
                                {/* FIX: Use helper function to hide Duration */}
                                {!isClassicInventory(inventoryType) && (
                                  <TableHead className="min-w-20">Duration</TableHead>
                                )}
                                <TableHead className="min-w-32">Action</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {billboards.map((billboard: any) => (
                                <React.Fragment key={billboard.id}>
                                  {/* Main Billboard Row */}
                                  <TableRow
                                    className={selectedBillboard?.id === billboard.id ? "bg-blue-50" : "hover:bg-gray-50 cursor-pointer"}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleBillboardSelect(billboard.id);
                                    }}
                                  >
                                    {/* <TableCell>
                                      <div className="flex items-center gap-2">
                                        {billboard.type === 'Package' ? (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              togglePackageExpansion(billboard.id);
                                            }}
                                            className="p-1 h-6 w-6"
                                          >
                                            {expandedPackages.includes(billboard.id) ?
                                              <ChevronUp className="h-4 w-4" /> :
                                              <ChevronDown className="h-4 w-4" />
                                            }
                                          </Button>
                                        ) : (
                                          <input
                                            type="checkbox"
                                            checked={selectedBillboards.includes(billboard.id)}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              handleBillboardSelect(billboard.id);
                                            }}
                                            className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                          />
                                        )}
                                      </div>
                                    </TableCell> */}
                                    <TableCell>
                                      <div className="flex items-center gap-3">
                                        {/* Image */}
                                        <img
                                          src={
                                            billboard.imageUrl ||
                                            `${import.meta.env.VITE_APP_FOLDER_PATH}/public/nomedia.jpg`
                                          }
                                          alt={billboard.name}
                                          className="w-12 h-8 sm:w-15 sm:h-10 rounded object-cover"
                                        />

                                        {/* Name + Media Owner stacked */}
                                        <div className="flex flex-col">
                                          <span className="font-medium text-sm sm:text-base">
                                            {billboard.name}
                                          </span>

                                          {/* Media Owner under the name */}
                                          <p className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">
                                            By {(billboard.type === "Package" || billboard.inventoryType?.toLowerCase() === "package" || billboard.packageBillboards?.length > 0)
                                              ? "Multiple Media Owners"
                                              : billboard.mediaOwnerName}
                                          </p>

                                          {/* Package info under media owner */}
                                          {(billboard.type === "Package" || billboard.inventoryType?.toLowerCase() === "package" || billboard.packageBillboards?.length > 0) && (
                                            <div className="text-xs text-purple-600 mt-1">
                                              Package ({packageInventories[billboard.id]?.length || billboard.packageBillboards?.length || 0} billboards)
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-sm">{inventoryType.toLowerCase() === 'package' ? 'Multiple Format' : billboard.format}</TableCell>
                                    <TableCell className="text-sm">{inventoryType.toLowerCase() === 'package' ? 'Multiple Resolution' : billboard.resolution}
                                    </TableCell>
                                    {/* FIX: Use helper function */}
                                    {!isClassicInventory(inventoryType) && (
                                      <TableCell className="text-sm">{inventoryType.toLowerCase() === 'package' ? 'Multiple Duration' : billboard.duration}</TableCell>
                                    )}
                                    <TableCell>
                                      {(billboard.type === 'Package' || billboard.inventoryType?.toLowerCase() === 'package' || billboard.packageBillboards?.length > 0) ? (
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-1">
                                          <span className="text-xs sm:text-sm text-gray-600">
                                            Package - {(() => {
                                              // Calculate total creatives from package inventories if available, otherwise from packageBillboards
                                              if (packageInventories[billboard.id]?.length > 0) {
                                                return packageInventories[billboard.id].reduce((sum: number, inventory: any) => {
                                                  const assignedCount = getAssignedCreativeCount(inventory.inventoryId, billboard.id);
                                                  return sum + assignedCount;
                                                }, 0);
                                              }
                                              // Fallback to packageBillboards if package inventories not loaded yet
                                              else if (billboard.packageBillboards?.length > 0) {
                                                return billboard.packageBillboards.reduce((sum: number, pb: any) => sum + pb.creativesUploaded, 0);
                                              }
                                              // Default to 0
                                              else {
                                                return 0;
                                              }
                                            })()} Total creatives
                                          </span>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="text-purple-600 hover:text-purple-700 border-purple-300 hover:border-purple-400 text-xs px-2 py-1"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              togglePackageExpansion(billboard.id);
                                            }}
                                          >
                                            <span>Assign Creatives</span>
                                            <ChevronDown
                                              className={`h-3 w-3 ml-1 transition-transform duration-200 ${expandedPackages.includes(billboard.id) ? 'rotate-180' : ''
                                                }`}
                                            />
                                          </Button>
                                        </div>
                                      ) : (
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-1">
                                          <span className="text-xs sm:text-sm text-gray-600">
                                            {(() => {
                                              const assignedCount = getAssignedCreativeCount(billboard.inventoryId);
                                              return `${assignedCount} Creative${assignedCount !== 1 ? 's' : ''} assigned`;
                                            })()}
                                          </span>
                                          <Button
                                            variant="default"
                                            size="sm"
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2 py-1"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenAssignModal(billboard);
                                            }}
                                          >
                                            Assign
                                          </Button>
                                        </div>
                                      )}
                                    </TableCell>
                                  </TableRow>

                                  {/* Package Individual Billboards */}
                                  {(billboard.type === 'Package' || billboard.inventoryType?.toLowerCase() === 'package' || billboard.packageBillboards?.length > 0) &&
                                    expandedPackages.includes(billboard.id) && (
                                      <>
                                        {/* Package Inventory Search */}
                                        <TableRow className="bg-purple-50/50">
                                          <TableCell colSpan={inventoryType.toLowerCase() === 'classic' ? 4 : 5} className="py-3">
                                            <div className="flex items-center gap-2 px-2">
                                              <Search className="h-4 w-4 text-gray-400" />
                                              <Input
                                                placeholder="Search package inventories..."
                                                value={packageInventorySearch}
                                                onChange={(e) => setPackageInventorySearch(e.target.value)}
                                                className="h-8 text-sm focus:ring-emerald-500"
                                              />
                                              {packageInventorySearch && (
                                                <button
                                                  onClick={() => setPackageInventorySearch("")}
                                                  className="text-gray-400 hover:text-emerald-600 transition-colors"
                                                  title="Clear search"
                                                >
                                                  <X className="h-4 w-4" />
                                                </button>
                                              )}
                                            </div>
                                          </TableCell>
                                        </TableRow>

                                        {/* Show loading state if data is being fetched */}
                                        {packageInventoriesLoading && !packageInventories[billboard.id] ? (
                                          <TableRow className="bg-purple-50/50">
                                            <TableCell colSpan={inventoryType.toLowerCase() === 'classic' ? 4 : 5} className="text-center py-4">
                                              <div className="flex items-center justify-center gap-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                                                <span className="text-sm text-gray-600">Loading package inventories...</span>
                                              </div>
                                            </TableCell>
                                          </TableRow>
                                        ) : packageInventories[billboard.id]?.length > 0 ? (
                                          (() => {
                                            // Filter package inventories based on search term
                                            const filteredPackageInventories = (packageInventories[billboard.id] || []).filter((packageInventory: any) => {
                                              if (!packageInventorySearch) return true;
                                              const searchLower = packageInventorySearch.toLowerCase();
                                              return (
                                                packageInventory.name?.toLowerCase().includes(searchLower) ||
                                                packageInventory.inventoryId?.toLowerCase().includes(searchLower) ||
                                                packageInventory.referenceId?.toLowerCase().includes(searchLower)
                                              );
                                            });

                                            // Show "no results" message if search yields no results
                                            if (filteredPackageInventories.length === 0) {
                                              return (
                                                <TableRow className="bg-purple-50/50">
                                                  <TableCell colSpan={inventoryType.toLowerCase() === 'classic' ? 4 : 5} className="text-center py-6">
                                                    <p className="text-sm text-gray-500">
                                                      No package inventories match "{packageInventorySearch}"
                                                    </p>
                                                  </TableCell>
                                                </TableRow>
                                              );
                                            }

                                            return filteredPackageInventories.map((packageInventory: any) => (
                                              <TableRow
                                                key={`${billboard.id}-${packageInventory.inventoryId}`}
                                                className={`bg-purple-50/50 hover:bg-purple-100/50 cursor-pointer ${selectedBillboard?.inventoryId === packageInventory.inventoryId ? "bg-blue-100" : ""}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  // Create a proper billboard object from package inventory and select it
                                                  const packageBillboard: BillboardInfo = {
                                                    ...billboard,
                                                    id: packageInventory.inventoryId,
                                                    name: packageInventory.name,
                                                    inventoryId: packageInventory.inventoryId,
                                                    inventoryType: packageInventory.type || 'digital',
                                                    resolution: packageInventory.specification?.resolution1Width && packageInventory.specification?.resolution1Height
                                                      ? `${packageInventory.specification.resolution1Width} X ${packageInventory.specification.resolution1Height}`
                                                      : packageInventory.size || billboard.resolution || 'N/A',
                                                    duration: String(packageInventory.spotDuration || billboard.duration || 'N/A'),
                                                    imageUrl: packageInventory.thumbnail || billboard.imageUrl,
                                                    // FIX: Use package inventory's format data
                                                    format: packageInventory.type?.toLowerCase() === 'digital'
                                                      ? [...(packageInventory.videoSupport || []), ...(packageInventory.imageSupport || [])].join(', ').toUpperCase() || 'DIGITAL'
                                                      : packageInventory.type?.toLowerCase() === 'classic'
                                                        ? (packageInventory.imageSupport || []).join(', ').toUpperCase() || 'STATIC'
                                                        : packageInventory.type?.toUpperCase() || billboard.format || 'N/A',
                                                    mediaOwnerId: packageInventory.mediaOwnerId || billboard.mediaOwnerId,
                                                    mediaOwnerName: packageInventory.mediaOwnerName || billboard.mediaOwnerName,
                                                    dealId: billboard.dealId,
                                                    startDate: billboard.startDate,
                                                    endDate: billboard.endDate,
                                                    inventoryDetails: {
                                                      resolution1Width: packageInventory.specification?.resolution1Width || '',
                                                      resolution1Height: packageInventory.specification?.resolution1Height || '',
                                                      panel1Width: packageInventory.specification?.panel1Width || '',
                                                      panel1Height: packageInventory.specification?.panel1Height || '',
                                                      videoSupport: packageInventory.videoSupport || [],
                                                      imageSupport: packageInventory.imageSupport || [],
                                                      panelFormat: packageInventory.panelFormat || billboard.inventoryDetails?.panelFormat || ''
                                                    }
                                                  };

                                                  // Select this package billboard and show its assigned creatives
                                                  setSelectedBillboards([packageInventory.inventoryId]);
                                                  setSelectedBillboard(packageBillboard);
                                                  handleBillboardClick(packageBillboard);
                                                }}
                                              >
                                                <TableCell className="min-w-48">
                                                  <div className="flex items-center gap-3">
                                                    <img
                                                      src={packageInventory.thumbnail}
                                                      alt={packageInventory.name}
                                                      className="w-10 h-6 sm:w-12 sm:h-8 rounded object-cover"
                                                    />
                                                    <div className="flex flex-col">
                                                      <span className="font-medium text-sm">{packageInventory.name}</span>
                                                      {/* <div className="text-xs text-gray-500 mt-1">
                                                {packageInventory.referenceId} • {packageInventory.venueType}
                                              </div>
                                              <div className="text-xs text-blue-600 mt-1">
                                                {packageInventory.type} • {packageInventory.group}
                                              </div> */}
                                                      {packageInventory.mediaOwnerName}

                                                    </div>
                                                  </div>
                                                </TableCell>
                                                <TableCell className="text-sm min-w-24">
                                                  {packageInventory.type?.toLowerCase() === 'digital'
                                                    ? [...(packageInventory?.videoSupport || []), ...(packageInventory?.imageSupport || [])].length > 0
                                                      ? [...(packageInventory?.videoSupport || []), ...(packageInventory?.imageSupport || [])].join(', ').toUpperCase()
                                                      : 'N/A'
                                                    : packageInventory.type?.toLowerCase() === 'classic'
                                                      ? (packageInventory?.imageSupport || []).length > 0
                                                        ? (packageInventory?.imageSupport || []).join(', ').toUpperCase()
                                                        : 'N/A'
                                                      : 'N/A'}
                                                </TableCell>
                                                <TableCell className="text-sm min-w-24">{packageInventory.size}</TableCell>
                                                {inventoryType.toLowerCase() !== 'classic' && (
                                                  <TableCell className="text-sm min-w-20">{packageInventory.spotDuration}s</TableCell>
                                                )}
                                                <TableCell className="min-w-32">
                                                  <div className="flex flex-col sm:flex-row sm:items-center gap-1">
                                                    <span className="text-xs sm:text-sm text-gray-600">
                                                      {(() => {
                                                        const assignedCount = getAssignedCreativeCount(packageInventory.inventoryId, billboard.id);
                                                        return `${assignedCount} Creative${assignedCount !== 1 ? 's' : ''} assigned`;
                                                      })()}
                                                    </span>
                                                    <Button
                                                      variant="default"
                                                      size="sm"
                                                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2 py-1"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        // Create a proper billboard object from package inventory
                                                        const packageBillboard: BillboardInfo = {
                                                          ...billboard,
                                                          id: packageInventory.inventoryId,
                                                          name: packageInventory.name,
                                                          inventoryId: packageInventory.inventoryId,
                                                          parentPackageId: billboard.inventoryId,
                                                          inventoryType: packageInventory.type || 'digital',
                                                          resolution: packageInventory.specification?.resolution1Width && packageInventory.specification?.resolution1Height
                                                            ? `${packageInventory.specification.resolution1Width}x${packageInventory.specification.resolution1Height}`
                                                            : billboard.resolution || 'N/A',
                                                          duration: String(packageInventory.spotDuration || 'N/A'),
                                                          imageUrl: packageInventory.thumbnail || billboard.imageUrl,

                                                          // ✅ FIX: Use package inventory's format data  
                                                          format: packageInventory.type?.toLowerCase() === 'digital'
                                                            ? [...(packageInventory.videoSupport || []), ...(packageInventory.imageSupport || [])].join(', ').toUpperCase()
                                                            : packageInventory.type?.toLowerCase() === 'classic'
                                                              ? (packageInventory.imageSupport || []).join(', ').toUpperCase()
                                                              : billboard.format || 'N/A',

                                                          inventoryDetails: {
                                                            resolution1Width: packageInventory.specification?.resolution1Width || '',
                                                            resolution1Height: packageInventory.specification?.resolution1Height || '',
                                                            panel1Width: '',
                                                            panel1Height: '',

                                                            // ✅ FIX: Use package inventory's own format support arrays
                                                            videoSupport: packageInventory.videoSupport || [],
                                                            imageSupport: packageInventory.imageSupport || [],

                                                            panelFormat: billboard.inventoryDetails?.panelFormat || ''
                                                          }
                                                        };
                                                        handleOpenAssignModal(packageBillboard);
                                                      }}
                                                    >
                                                      Assign
                                                    </Button>
                                                  </div>
                                                </TableCell>
                                              </TableRow>
                                            ));
                                          })()
                                        ) : (
                                          <TableRow className="bg-purple-50/50">
                                            <TableCell colSpan={inventoryType.toLowerCase() === 'classic' ? 4 : 5} className="text-center py-4">
                                              <span className="text-sm text-gray-500">No package inventories found.</span>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </>
                                    )}
                                </React.Fragment>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ));
              })()}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col items-center gap-2 mt-6">
                <div className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={handlePreviousPage}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  {/* Always show current page */}
                  <Button variant="default" size="sm">{currentPage}</Button>

                  {/* Show next page if not the last page */}
                  {currentPage < totalPages && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      {currentPage + 1}
                    </Button>
                  )}

                  {/* Show ellipsis if there's a gap between next page and last page */}
                  {currentPage + 1 < totalPages - 1 && (
                    <Button variant="outline" size="sm" disabled>...</Button>
                  )}

                  {/* Show last page if it's not the current page and not the next page */}
                  {totalPages > 1 && currentPage + 1 < totalPages && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={handleNextPage}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Assign Button - Single row logic only, multiple selection logic commented out */}
            {/*
            <div className="flex justify-center mt-6">
              <Button
                // onClick={handleAssignCreative}
                onClick={() => setShowAssignModal(true)}
                disabled={
                  !isMultiSelectValid
                }
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
              >
                Assign creative
              </Button>
            </div>
            */}
          </div>

          {/* Right Column - Assigned Creatives */}
          <div>
            <Card className="border border-gray-200 shadow-sm mb-6">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-emerald-600">Assigned creatives</h2>
                  <div className="flex flex-col items-end">
                    <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
                      {/* <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-gray-300"
                      disabled={selectedBillboards.length === 0}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload
                    </Button>
                  </DialogTrigger> */}
                      <DialogContent className="max-w-7xl h-[90vh] flex flex-col">
                        <DialogHeader className="sticky top-0 text-left bg-white border-b pb-4">
                          <DialogTitle>Upload creatives</DialogTitle>
                        </DialogHeader>

                        <div className="flex-1 overflow-y-auto p-6">
                          <div className="grid grid-cols-1 md:grid-cols-[13fr,7fr] gap-6">
                            {/* Upload Area (Left Side - Slightly Narrower) */}
                            <div>
                              <div
                                className={`border-2 border-dashed rounded-lg p-8 text-center ${selectedBillboards.length === 0
                                  ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                  : dragActive
                                    ? 'border-emerald-500 bg-emerald-50'
                                    : 'border-gray-300'
                                  }`}
                                onDragEnter={selectedBillboards.length > 0 ? handleDrag : undefined}
                                onDragLeave={selectedBillboards.length > 0 ? handleDrag : undefined}
                                onDragOver={selectedBillboards.length > 0 ? handleDrag : undefined}
                                onDrop={selectedBillboards.length > 0 ? handleDrop : undefined}
                              >
                                <CloudUpload
                                  className={`h-12 w-12 mx-auto mb-4 ${selectedBillboards.length === 0 ? 'text-gray-300' : 'text-gray-400'
                                    }`}
                                />
                                <p
                                  className={`text-lg font-medium mb-2 ${selectedBillboards.length === 0 ? 'text-gray-400' : 'text-gray-700'
                                    }`}
                                >
                                  {selectedBillboards.length === 0
                                    ? 'Select at least one Inventory to upload creatives'
                                    : 'Drag & drop files or creatives from content hub'}
                                </p>
                                {selectedBillboards.length > 0 && (
                                  <>
                                    <p className="text-xs text-gray-500">700 X 370</p>
                                    <p className="text-xs text-emerald-600">mp4.jpg</p>
                                  </>
                                )}

                                <div className="mt-4">
                                  <input
                                    type="file"
                                    multiple
                                    accept="image/*,video/*"
                                    onChange={(e) => {
                                      handleFileUpload(e.target.files);
                                      e.target.value = '';
                                    }}
                                    className="hidden"
                                    id="file-upload"
                                    disabled={selectedBillboards.length === 0}
                                  />
                                  <label
                                    htmlFor="file-upload"
                                    className={`${selectedBillboards.length === 0
                                      ? 'opacity-50 cursor-not-allowed'
                                      : 'cursor-pointer'
                                      }`}
                                  >
                                    <Button
                                      variant="outline"
                                      className={`${selectedBillboards.length === 0
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'border-emerald-300 text-emerald-600 hover:bg-emerald-50'
                                        }`}
                                      disabled={selectedBillboards.length === 0}
                                      asChild
                                    >
                                      <span>
                                        <Upload className="h-4 w-4 mr-2" />
                                        Browse Files
                                      </span>
                                    </Button>
                                  </label>
                                </div>
                              </div>

                              {/* Uploaded Files List */}
                              {uploadedFiles.length > 0 && (
                                <div className="mt-4">
                                  <div className="overflow-x-auto">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead className="w-48">Creative</TableHead>
                                          <TableHead className="w-32">Format</TableHead>
                                          <TableHead className="w-28">Resolution</TableHead>
                                          <TableHead className="w-40">Action</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {uploadedFiles.map((file) => (
                                          <TableRow key={file.id}>
                                            <TableCell className="w-48">
                                              <div className="flex items-center gap-3">
                                                <img
                                                  src={file.thumbnail}
                                                  alt={file.name}
                                                  className="w-8 h-8 rounded object-cover flex-shrink-0"
                                                />
                                                <span
                                                  className="text-sm truncate min-w-0"
                                                  title={file.name}
                                                >
                                                  {file.name}
                                                </span>
                                              </div>
                                            </TableCell>
                                            <TableCell className="w-32 text-sm">{file.format}</TableCell>
                                            <TableCell className="w-28 text-sm">
                                              {file.resolution}
                                            </TableCell>
                                            <TableCell className="w-40">
                                              <div className="flex gap-1">
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="h-8 px-2"
                                                  onClick={() => {
                                                    setPreviewFile(file);
                                                    setAssignTab('preview');
                                                  }}
                                                >
                                                  📄
                                                  <span className="ml-1 text-xs">Show</span>
                                                </Button>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="h-8 px-2"
                                                  onClick={() => handleDeleteUploaded(file.id)}
                                                >
                                                  <Trash2 className="h-3 w-3 text-red-600" />
                                                  <span className="ml-1 text-xs text-red-600">
                                                    Delete
                                                  </span>
                                                </Button>
                                              </div>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Selected Creatives Table */}
                            {selectedCreatives.length > 0 && (
                              <div className="mt-4">
                                <h4 className="font-medium mb-2">Selected Creatives for Assignment</h4>
                                <div className="overflow-x-auto">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead className="w-48">Creative</TableHead>
                                        <TableHead className="w-32">Format</TableHead>
                                        <TableHead className="w-28">Resolution</TableHead>
                                        <TableHead className="w-40">Action</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {selectedCreatives.map((creativeId) => {
                                        const creative = creatives.find(c => c.id === creativeId);
                                        if (!creative) return null;
                                        return (
                                          <TableRow key={creative.id}>
                                            <TableCell className="w-48">
                                              <div className="flex items-center gap-3">
                                                <img
                                                  src={creative.thumbnailUrl}
                                                  alt={creative.name}
                                                  className="w-8 h-8 rounded object-cover flex-shrink-0"
                                                />
                                                <div className="flex-1 min-w-0">
                                                  <p className="text-sm font-medium truncate" title={creative.name}>
                                                    {creative.name}
                                                  </p>
                                                </div>
                                              </div>
                                            </TableCell>
                                            <TableCell className="w-32 text-sm">{creative.format}</TableCell>
                                            <TableCell className="w-28 text-sm">{creative.resolution}</TableCell>
                                            <TableCell className="w-40">
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 px-2"
                                                onClick={() =>
                                                  setSelectedCreatives(prev =>
                                                    prev.filter(id => id !== creative.id)
                                                  )
                                                }
                                              >
                                                <Trash2 className="h-3 w-3 text-red-600" />
                                                <span className="ml-1 text-xs text-red-600">
                                                  Remove
                                                </span>
                                              </Button>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </div>
                              </div>
                            )}

                            {/* Content Hub (Right Side - Slightly Wider) */}
                            <div>
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="font-medium">Content hub</h3>
                              </div>

                              <div className="relative mb-4">
                                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                <Input
                                  placeholder="Search creatives"
                                  className="pl-10 px-5 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                              </div>

                              <p className="text-xs text-gray-500 mb-4">
                                Supported resolution are displayed in the Requirement Details window
                                based on your location
                              </p>

                              <div className="grid grid-cols-2 gap-2 mb-4">
                                {creatives.slice(0, 3).map((creative: CreativeItem) => (
                                  <div
                                    key={creative.id}
                                    className="relative border rounded p-2 cursor-move hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
                                    draggable={selectedBillboards.length > 0}
                                    onDragStart={(e) => {
                                      if (selectedBillboards.length === 0) {
                                        e.preventDefault();
                                        return;
                                      }
                                      e.dataTransfer.setData("application/json", JSON.stringify(creative));
                                      e.dataTransfer.effectAllowed = "copy";
                                    }}
                                    onDragEnd={() => {
                                      console.info("Drag ended for creative");
                                    }}
                                    title={
                                      selectedBillboards.length === 0
                                        ? 'Select a billboard first to drag creatives'
                                        : `Drag ${creative.name} to upload area`
                                    }
                                  >
                                    <img
                                      src={creative.thumbnailUrl}
                                      alt={creative.name}
                                      className="w-full h-20 object-cover rounded mb-2"
                                    />
                                    <p className="text-xs font-medium">{creative.name}</p>
                                    <p className="text-xs text-gray-500">{creative.mimeType}</p>
                                    <p className="text-xs text-gray-500">{creative.resolution}</p>
                                    {selectedBillboards.length === 0 && (
                                      <div className="absolute inset-0 bg-gray-200 bg-opacity-75 rounded flex items-center justify-center">
                                        <p className="text-xs text-gray-600 text-center px-2">
                                          Select billboard first
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>

                              {/* Pagination */}
                              <div className="flex items-center justify-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={popupCurrentPage <= 1}
                                  onClick={handlePopupPreviousPage}
                                >
                                  <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button variant="default" size="sm">{popupCurrentPage}</Button>
                                {popupTotalPages > 1 && popupCurrentPage < popupTotalPages && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPopupCurrentPage(popupCurrentPage + 1)}
                                  >
                                    {popupCurrentPage + 1}
                                  </Button>
                                )}
                                {popupTotalPages > 2 && popupCurrentPage < popupTotalPages - 1 && (
                                  <Button variant="outline" size="sm">...</Button>
                                )}
                                {popupTotalPages > 1 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPopupCurrentPage(popupTotalPages)}
                                  >
                                    {popupTotalPages}
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={popupCurrentPage >= popupTotalPages}
                                  onClick={handlePopupNextPage}
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="sticky z-10 bg-white border-t pt-4 flex justify-between">
                          <Button variant="outline" onClick={() => setShowUploadModal(false)}>
                            Close
                          </Button>
                          <Button
                            onClick={() => {
                              setShowUploadModal(false);
                              if (uploadedFiles.length > 0) {
                                toast({
                                  title: "Success",
                                  description: `Applied ${uploadedFiles.length} uploaded files to creative library`,
                                  variant: "default",
                                });
                                setUploadedFiles([]);
                              }
                            }}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            Apply
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                {selectedBillboards.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Select at least one Inventory to upload creatives
                  </p>
                )}
                {selectedBillboards.length > 0 && (
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search Assigned Creatives"
                      value={searchCreative}
                      onChange={(e) => setSearchCreative(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          setSearchCreativeTerm(searchCreative.trim());
                        }
                      }}
                      className="pl-10 pr-10 px-5 focus:ring-emerald-500"
                    />
                    {(searchCreativeTerm || searchCreative) && (
                      <button
                        onClick={() => {
                          setSearchCreative('');
                          setSearchCreativeTerm('');
                        }}
                        className="absolute right-3 top-3 text-gray-400 hover:text-emerald-600 transition-colors"
                        title="Clear search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-sm mb-6">
              <div className="p-6">
                {selectedBillboards.length > 0 && (() => {
                  const type = (selectedBillboard?.inventoryType || selectedBillboard?.type)?.toLowerCase();
                  if (type !== 'package') {
                    return (
                      <div className="mb-6">
                        <h3 className="font-medium mb-3 text-emerald-600">Selected Inventory ({selectedBillboards.length})</h3>
                        <div className="space-y-2">
                          {getAllBillboardsById(selectedBillboards).map((billboard) => (
                            <div key={billboard.id} className="flex items-center gap-3 p-2 bg-emerald-50 rounded">
                              <div className="flex-1">
                                <p className="text-sm font-medium">{billboard.name}</p>
                                <p className="text-xs text-gray-500">{billboard.format} • {billboard.resolution}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {selectedBillboards.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Select a Inventory to view assigned creatives</p>
                  </div>
                ) : isLoading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                    <p className="text-gray-500 mt-2">Loading assigned creatives...</p>
                  </div>
                ) : selectedBillboards.length === 1 && billboardAssignedCreatives.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No creatives assigned to this Inventory</p>
                  </div>
                ) : selectedBillboards.length === 1 && filteredBillboardAssignedCreatives.length === 0 && searchCreativeTerm.trim() ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No creatives found matching "{searchCreativeTerm}"</p>
                    <button
                      onClick={() => {
                        setSearchCreative('');
                        setSearchCreativeTerm('');
                      }}
                      className="text-emerald-600 hover:text-emerald-700 text-sm mt-2 underline"
                    >
                      {/* Clear search to show all creatives */}
                    </button>
                  </div>
                ) : selectedBillboards.length === 1 ? (
                  <div className="space-y-6">
                    {/* Show assigned creatives for single selected billboard */}
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          {/* <h3 className="font-medium text-gray-900">{selectedBillboard?.name}</h3> */}
                          {searchCreativeTerm.trim() && (
                            <span className="text-xs text-gray-500">
                              {filteredBillboardAssignedCreatives.length} of {billboardAssignedCreatives.length} creatives
                            </span>
                          )}
                        </div>

                        {/* Package Cart Info - Creative Count and Inventory Count */}
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <span className="text-gray-600">Creative Count:</span>
                            <span className="font-medium text-emerald-600">
                              {(() => {
                                const creativeCount = getAssignedCreativeCount(selectedBillboard?.inventoryId || '');
                                return creativeCount || filteredBillboardAssignedCreatives.length;
                              })()}
                            </span>
                          </div>

                          <div className="flex items-center gap-1">
                            <span className="text-gray-600">Inventory:</span>
                            <span className="font-medium text-blue-600">
                              {selectedBillboard?.inventoryType?.toLowerCase() === 'package' ? (
                                (() => {
                                  // For packages, show the breakdown from package inventories
                                  const packageInventoryArray = packageInventories[selectedBillboard?.id || ''];
                                  if (packageInventoryArray && packageInventoryArray.length > 0) {
                                    const digitalCount = packageInventoryArray.filter((bb: any) => bb.type === 'digital').length;
                                    const classicCount = packageInventoryArray.filter((bb: any) => bb.type === 'classic').length;
                                    const parts = [];
                                    if (digitalCount > 0) parts.push(`${digitalCount} Digital`);
                                    if (classicCount > 0) parts.push(`${classicCount} Classic`);
                                    return parts.length > 0 ? parts.join(', ') : 'No inventories';
                                  }
                                  return 'Package';
                                })()
                              ) : (
                                (() => {
                                  const type = (selectedBillboard?.inventoryType || selectedBillboard?.type || '').toLowerCase();
                                  if (type === 'digital' || type === 'digital_billboard') return 'Digital';
                                  if (type === 'classic' || type === 'static') return 'Classic';
                                  if (type === 'package') return 'Package';
                                  return 'Unknown';
                                })()
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {filteredBillboardAssignedCreatives.map((creative) => (
                          <div
                            key={creative.id}
                            className="relative border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => handleCreativeClick(creative)}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <img
                                src={creative.thumbnailUrl}
                                alt={creative.name}
                                className="w-10 h-10 rounded object-cover"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate"
                                  title={creative.name}>{creative.name}</p>
                                <p className="text-xs text-gray-500">{creative.format}</p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent triggering the card click
                                  handleRemoveCreative(selectedBillboard?.inventoryId || '', String(creative.id));
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>

                            {/* Status Badge */}
                            <div className="flex items-center gap-2">
                              <div className="w-40 h-8 mt-1.5">
                                <StatusBadge status={creative.status || 'pending'} />
                              </div>

                              {creative.status?.toLowerCase() === 'rejected' && (
                                <div className="w-40">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedRejectionReason(creative.comments || 'No reason provided');
                                      setShowRejectionReasonModal(true);
                                    }}
                                    className="w-full h-full p-1 text-xs text-red-700 hover:text-red-800 hover:bg-red-100 font-normal bg-red-50 border border-red-200 rounded-md"
                                  >
                                    <span className="truncate block text-left">
                                      Reason
                                    </span>
                                  </Button>
                                </div>
                              )}
                            </div>

                            <div className="text-xs text-gray-500">
                              <p>Resolution: {creative.resolution}</p>
                              <p>Duration: {creative.duration}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : assignedCreatives.size === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No creatives assigned to selected billboards</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Group creatives by billboard */}
                    {getAllBillboardsById(selectedBillboards).map((billboard) => {
                      const creativesForBillboard = assignedCreatives.get(billboard.id) || [];
                      if (creativesForBillboard.length === 0) return null;
                      return (
                        <div key={billboard.id} className="space-y-3">
                          <div className="space-y-2">
                            <h3 className="font-medium text-gray-900">{billboard.name}</h3>

                            {/* Package Cart Info - Creative Count and Inventory Count for Multiple Selection */}
                            <div className="flex items-center gap-4 text-sm">
                              <div className="flex items-center gap-1">
                                <span className="text-gray-600">Creative Count:</span>
                                <span className="font-medium text-emerald-600">
                                  {(() => {
                                    const creativeCount = getAssignedCreativeCount(billboard.inventoryId || '');
                                    return creativeCount;
                                  })()}
                                </span>
                              </div>

                              <div className="flex items-center gap-1">
                                <span className="text-gray-600">Inventory:</span>
                                <span className="font-medium text-blue-600">
                                  {billboard.inventoryType === 'package' ? (
                                    (() => {
                                      // For packages, show the breakdown from package inventories
                                      const packageInventoryArray = packageInventories[billboard.id || ''];
                                      if (packageInventoryArray && packageInventoryArray.length > 0) {
                                        const digitalCount = packageInventoryArray.filter((bb: any) => bb.type === 'digital').length;
                                        const classicCount = packageInventoryArray.filter((bb: any) => bb.type === 'classic').length;
                                        const parts = [];
                                        if (digitalCount > 0) parts.push(`${digitalCount} Digital`);
                                        if (classicCount > 0) parts.push(`${classicCount} Classic`);
                                        return parts.length > 0 ? parts.join(', ') : 'No inventories';
                                      }
                                      return 'Package';
                                    })()
                                  ) : (
                                    billboard.inventoryType === 'digital' ? 'Digital' :
                                      billboard.inventoryType === 'classic' ? 'Classic' :
                                        'Unknown'
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {creativesForBillboard.map((creative) => (
                              <div
                                key={creative.id}
                                className="relative border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
                                onClick={() => handleCreativeClick(creative)}
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <img
                                    src={creative.thumbnailUrl}
                                    alt={creative.name}
                                    className="w-10 h-10 rounded object-cover"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate" title={creative.name}>{creative.name}</p>
                                    <p className="text-xs text-gray-500">{creative.format}</p>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveCreative(billboard.inventoryId || '', String(creative.id));
                                    }}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                                {/* Status Badge */}
                                <div className="flex items-center gap-2">
                                  <div className="w-40 h-8 mt-1.5">
                                    <StatusBadge status={creative.status || 'pending'} />
                                  </div>
                                  {creative.status?.toLowerCase() === 'rejected' && (
                                    <div className="w-40">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedRejectionReason(creative.comments || 'No reason provided');
                                          setShowRejectionReasonModal(true);
                                        }}
                                        className="w-full h-full p-1 text-xs text-red-700 hover:text-red-800 hover:bg-red-100 font-normal bg-red-50 border border-red-200 rounded-md"
                                      >
                                        <span className="truncate block text-left">
                                          Rejected Reason
                                        </span>
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">
                                  <p>Resolution: {creative.resolution}</p>
                                  <p>Duration: {creative.duration}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={(open) => {
        if (!open) {
          setShowSuccessModal(false);
          // Refresh the page to update creative counts
          window.location.reload();
        }
      }}>
        <DialogContent className="max-w-md">
          <div className="text-center py-6">
            <div className="mx-auto mb-4">
              <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Creatives Uploaded Successfully!</h3>
            <p className="text-gray-600 mb-6">{successMessage || "Successfully assigned creatives to billboards"}</p>
            <Button
              onClick={() => {
                setShowSuccessModal(false);
                // Refresh the page to update creative counts
                window.location.reload();
              }}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Billboard Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Edit Creative Assignment</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 sm:space-y-6">
            {/* Billboard Info */}
            <div className="bg-emerald-50 p-3 sm:p-4 rounded-lg">
              <h3 className="font-medium text-emerald-900 mb-2 text-sm sm:text-base">Billboard Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <span className="text-gray-600">Format:</span>
                  <p className="font-medium">N/A</p>
                </div>
                <div>
                  <span className="text-gray-600">Resolution:</span>
                  <p className="font-medium">N/A</p>
                </div>
                <div>
                  <span className="text-gray-600">Duration:</span>
                  <p className="font-medium">N/A</p>
                </div>
              </div>
            </div>

            {/* Upload Area */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6">
              <div className="text-center">
                <CloudUpload className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-4 text-gray-400" />
                <p className="text-sm sm:text-lg font-medium mb-1 sm:mb-2">Upload New Creative</p>
                <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">Drag & drop files or browse</p>
                <div className="space-y-2">
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                    id="edit-file-upload"
                  />
                  <label htmlFor="edit-file-upload">
                    <Button variant="outline" className="cursor-pointer text-xs sm:text-sm" asChild>
                      <span>Browse Files</span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>

            {/* Currently Assigned Creatives */}
            <div>
              <h3 className="font-medium mb-3 sm:mb-4 text-sm sm:text-base">Currently Assigned Creatives</h3>
              {false ? ( // editingBillboard disabled
                <div className="text-center py-6 sm:py-8 text-gray-500 text-sm">
                  No creatives assigned to this billboard
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {[].map((creative: any) => ( // editingBillboard disabled
                    <div key={creative.id} className="border rounded-lg p-2 sm:p-3 space-y-2">
                      <img
                        src={creative.thumbnail}
                        alt={creative.name}
                        className="w-full h-20 sm:h-24 object-cover rounded"
                      />
                      <div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate" title={creative.name}>
                            {creative.name}
                          </p>
                        </div>
                        <p className="text-xs text-gray-500">{creative.resolution}</p>
                        <p className="text-xs text-orange-600">{creative.status}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-red-600 hover:bg-red-50 text-xs sm:text-sm"
                        onClick={() => handleDeleteCreative(String(creative.id))}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-4 sm:mt-6">
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="text-xs sm:text-sm">
              Cancel
            </Button>
            <Button onClick={() => setShowEditModal(false)} className="bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Creatives Modal */}
      <Dialog
        open={showAssignModal}
        onOpenChange={(open) => {
          setShowAssignModal(open);
          if (!open) {
            setUploadedFiles([]); // Clear uploaded files when modal closes
          }
        }}
      >
        <DialogContent className="max-w-7xl h-[90vh] flex flex-col">
          <DialogHeader className="sticky top-0 text-left bg-white border-b pb-4">
            <DialogTitle className="text-emerald-600">
              {selectedBillboards.length > 1
                ? `Assign & Upload Creatives to ${selectedBillboards.length} Billboards`
                : `Assign & Upload Creatives to ${selectedBillboard?.name || 'Billboard'}`}
            </DialogTitle>
          </DialogHeader>


          <div className="flex-1 overflow-y-auto p-6">
            {assignTab === 'preview' && previewFile ? (
              <div>
                <Button
                  variant="outline"
                  className="mb-4"
                  onClick={() => setAssignTab('list')}
                >
                  ← Back
                </Button>
                {/* Preview media at the top */}
                <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center mb-6" style={{ minHeight: '320px' }}>
                  <div className="w-full max-w-xl aspect-video bg-black flex items-center justify-center">
                    {(() => {
                      if (!previewFile?.filePath) {
                        console.warn('[Preview Render] No filePath for previewFile:', previewFile);
                        return null;
                      }
                      const fileUrl = previewFile.filePath;
                      const fileName = previewFile.fileName;
                      const ext = fileUrl.split('.').pop()?.toLowerCase();
                      const videoExts = ['mp4', 'mov', 'webm', 'avi', 'mkv'];
                      if (videoExts.includes(ext || '')) {
                        return (
                          <video
                            src={fileUrl}
                            className="w-full h-full object-contain"
                            controls
                            preload="metadata"
                            style={{ background: 'black' }}
                          />
                        );
                      } else {
                        return (
                          <img
                            src={fileUrl}
                            alt={fileName}
                            className="w-full h-full object-contain"
                            style={{ background: 'black' }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`;
                            }}
                          />
                        );
                      }
                    })()}
                  </div>
                </div>
                {/* Details below preview */}
                {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Name:</label>
                    <p className="text-sm text-gray-900">{previewFile.fileName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Format:</label>
                    <p className="text-sm text-gray-900">{previewFile.mimeType}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Resolution:</label>
                    <p className="text-sm text-gray-900">{previewFile.resolution}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Duration:</label>
                    <p className="text-sm text-gray-900">{previewFile.duration}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-600">File Path:</label>
                    <p className="text-xs text-blue-700 break-all">{previewFile.filePath}</p>
                  </div>
                </div> */}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-[3fr,2fr] gap-6">

                {/* Upload Area (Left Side) */}
                <div>
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center fixed top-20 left-6 right-[calc(40%+1.5rem)] z-50 bg-white shadow-lg ${selectedBillboards.length === 0
                      ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                      : dragActive
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-300'
                      }`}
                    onDragEnter={selectedBillboards.length > 0 ? handleDrag : undefined}
                    onDragLeave={selectedBillboards.length > 0 ? handleDrag : undefined}
                    onDragOver={selectedBillboards.length > 0 ? handleDrag : undefined}
                    onDrop={selectedBillboards.length > 0 ? handleDrop : undefined}
                  >
                    <CloudUpload
                      className={`h-12 w-12 mx-auto mb-4 ${selectedBillboards.length === 0 ? 'text-gray-300' : 'text-gray-400'
                        }`}
                    />
                    <p
                      className={`text-lg font-medium mb-2 ${selectedBillboards.length === 0 ? 'text-gray-400' : 'text-gray-700'
                        }`}
                    >
                      {selectedBillboards.length === 0
                        ? 'Select at least one Inventory to upload creatives'
                        : 'Drag & drop files or creatives from content hub'}
                    </p>
                    {selectedBillboards.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {(() => {
                          const first = getAllBillboardsById(selectedBillboards)[0];
                          return (
                            <>
                              <p className="text-xs text-gray-500">
                                Resolution: {selectedBillboard?.resolution || 'N/A'}
                              </p>
                              <p className="text-xs text-emerald-600">
                                Format: {selectedBillboard?.format || 'N/A'}
                              </p>
                            </>
                          );
                        })()}
                      </div>
                    )}

                    <div className="mt-4">
                      <input
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        onChange={(e) => {
                          handleFileUpload(e.target.files);
                          e.target.value = '';
                        }}
                        className="hidden"
                        id="file-upload"
                        disabled={selectedBillboards.length === 0}
                      />
                      <label
                        htmlFor="file-upload"
                        className={`${selectedBillboards.length === 0
                          ? 'opacity-50 cursor-not-allowed'
                          : 'cursor-pointer'
                          }`}
                      >
                        <Button
                          variant="outline"
                          className={`${selectedBillboards.length === 0
                            ? 'opacity-50 cursor-not-allowed'
                            : 'border-emerald-300 text-emerald-600 hover:bg-emerald-50'
                            }`}
                          disabled={selectedBillboards.length === 0}
                          asChild
                        >
                          {/* <span>
                            <Upload className="h-4 w-4 mr-2" />
                            Browse Files
                          </span> */}
                        </Button>
                      </label>
                    </div>
                  </div>

                  {/* Placeholder to maintain space for floating box */}
                  <div className="h-40"></div>

                  {/* Uploaded Files List */}
                  {uploadedFiles.length > 0 && (
                    <div className="mt-20 fixed yb">
                      <div className="max-h-[300px] overflow-y-auto rounded-lg bg-white">
                        <Table>
                          <TableHeader className="bg-gray-50">
                            <TableRow>
                              <TableHead className="w-48 font-semibold">Creative</TableHead>
                              <TableHead className="w-32 font-semibold">Format</TableHead>
                              <TableHead className="w-28 font-semibold">Resolution</TableHead>
                              <TableHead className="w-40 font-semibold">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                        </Table>
                      </div>

                      {/* Scrollable Table Body */}
                      <div className="max-h-48 overflow-y-auto">
                        <Table>
                          <TableBody>
                            {uploadedFiles.map((file) => (
                              <TableRow key={file.id}>
                                <TableCell className="w-48">
                                  <div className="flex items-center gap-3">
                                    <img
                                      src={file.thumbnail || file.thumbnailUrl}
                                      alt={file.name}
                                      className="w-8 h-8 rounded object-cover flex-shrink-0"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).src = `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`;
                                      }}
                                    />
                                    <span
                                      className="text-sm inline-block"
                                      title={file.name}
                                    >
                                      {file.name.length > 15 ? `${file.name.slice(0, 15)}...` : file.name}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell className="w-32 text-sm">{file.format}</TableCell>
                                <TableCell className="w-28 text-sm">{file.resolution}</TableCell>
                                <TableCell className="w-40">
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 px-2"
                                      onClick={() => {
                                        setPreviewFile(file);
                                        setAssignTab('preview');
                                      }}
                                    >
                                      {/* 📄 */}
                                      <span className="ml-1 text-xs">Show</span>
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 px-2"
                                      onClick={() => handleDeleteUploaded(file.id)}
                                    >
                                      <Trash2 className="h-3 w-3 text-red-600" />
                                      <span className="ml-1 text-xs text-red-600">
                                        Delete
                                      </span>
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>

                {/* Assign Creatives Area (Right Side) */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-emerald-600">Content Hub</h3>
                  </div>
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search creatives..."
                      value={popupSearchInput}
                      onChange={(e) => setPopupSearchInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          setPopupSearchTerm(popupSearchInput.trim());
                        }
                      }}
                      className="pl-10 pr-10 px-5 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    {(popupSearchTerm || popupSearchInput) && (
                      <button
                        onClick={() => {
                          setPopupSearchInput('');
                          setPopupSearchTerm('');
                        }}
                        className="absolute right-3 top-3 text-gray-400 hover:text-emerald-600 transition-colors"
                        title="Clear search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                    {(() => {
                      // Apply billboard-specific filtering to creatives
                      let displayCreatives = filteredPopupCreatives || creatives;
                      if (selectedBillboard) {
                        // const type = selectedBillboard.inventoryType?.toLowerCase();
                        const type = (
                          selectedBillboard?.inventoryType ||
                          selectedBillboard?.type
                        )?.toLowerCase();
                        if (type === 'classic') {
                          // Classic: images only, any type/resolution, no videos
                          displayCreatives = displayCreatives.filter((creative) => {
                            const isImage = creative.mimeType && creative.mimeType.startsWith('image/');
                            return isImage;
                          });
                        } else if (type === 'digital') {
                          // Digital: images and videos, strict validation
                          const billboardFormats = selectedBillboard.format
                            ? selectedBillboard.format.toLowerCase().split(',').map(f => f.trim())
                            : [];
                          const billboardDuration = parseFloat(selectedBillboard.duration);
                          const billboardResolution = selectedBillboard.resolution
                            ? selectedBillboard.resolution.toLowerCase().replace(/\s+/g, '')
                            : '';

                          displayCreatives = displayCreatives.filter((creative) => {
                            const mimeType = creative.mimeType?.toLowerCase() || '';
                            const isImage = mimeType.startsWith('image/');
                            const isVideo = mimeType.startsWith('video/');
                            const creativeResolution = creative.resolution?.toLowerCase().replace(/\s+/g, '') || '';

                          // For images: must match resolution and supported format
                            if (isImage) {
                              const formatMatched = billboardFormats.some(format => mimeType.includes(format));
                              const resolutionMatched = billboardResolution && creativeResolution && billboardResolution === creativeResolution;
                              return formatMatched && resolutionMatched;
                            }
                            // For videos: must match resolution, supported format, and duration within ±0.5
                            if (isVideo) {
                              const formatMatched = billboardFormats.some(format => mimeType.includes(format));
                              const resolutionMatched = billboardResolution && creativeResolution && billboardResolution === creativeResolution;
                              const creativeDuration = parseFloat(creative.duration);
                              const durationMatched = !isNaN(billboardDuration) && !isNaN(creativeDuration) && Math.abs(billboardDuration - creativeDuration) <= 0.5;
                              return formatMatched && resolutionMatched && durationMatched;
                            }
                            return false;
                          });
                        }
                      }
                    const creativesToShow = displayCreatives;
                    
                    // Check if search term exists but no results found
                    if (popupSearchTerm.trim() && creativesToShow.length === 0) {
                      return (
                        <div className="col-span-full text-center py-12">
                          <p className="text-gray-500">No creatives found matching "{popupSearchTerm}"</p>
                          {/* <button
                            onClick={() => setPopupSearchTerm('')}
                            className="text-emerald-600 hover:text-emerald-700 text-sm mt-2 underline"
                          >
                            Clear search to show all creatives
                          </button> */}
                        </div>
                      );
                    }
                    
                    return (
                      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4">
                        {(() => {
                      const slots = Array.from({ length: assignCreativesPerPage });
                      return slots.map((_, idx) => {
                        const creative = creativesToShow[idx];
                        if (!creative) return null;
                        return (
                          <div
                            key={creative.id}
                            className="border rounded-lg p-3 cursor-move transition-all border-gray-200 hover:border-gray-300"
                            draggable={selectedBillboards.length > 0}
                            onDragStart={(e) => {
                              if (selectedBillboards.length === 0) {
                                e.preventDefault();
                                return;
                              }
                              e.dataTransfer.setData("application/json", JSON.stringify(creative));
                              e.dataTransfer.effectAllowed = "copy";
                            }}
                            title={
                              selectedBillboards.length === 0
                                ? 'Select a billboard first to drag creatives'
                                : `Drag ${creative.name} to upload area`
                            }
                          >
                            <div className="aspect-video bg-gray-100 rounded mb-2 overflow-hidden">
                              <img
                                src={creative.thumbnailUrl}
                                alt={creative.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`;
                                }}
                              />
                            </div>
                            <h4 className="text-sm font-medium truncate">{creative.name}</h4>
                            <p className="text-xs text-gray-500">{creative.resolution}</p>
                            {creative.duration !== 'N/A' && (
                              <p className="text-xs text-gray-500">{creative.duration}s</p>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                );
              })()}

                  {/* Pagination for Assign Creatives */}
                  {(() => {
                    // Use backend-driven pagination for Assign Creatives modal
                    return popupTotalPages > 0 && (
                      <div className="flex items-center justify-between px-2 py-4">
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPopupCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={popupCurrentPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Previous
                          </Button>
                          <div className="flex items-center space-x-1">
                            {Array.from({ length: popupTotalPages }, (_, i) => i + 1)
                              .filter(page => {
                                return page === 1 ||
                                  page === popupTotalPages ||
                                  Math.abs(page - popupCurrentPage) <= 1;
                              })
                              .map((page, index, array) => (
                                <React.Fragment key={page}>
                                  {index > 0 && array[index - 1] < page - 1 && (
                                    <span className="px-2 text-gray-500">...</span>
                                  )}
                                  <Button
                                    variant={popupCurrentPage === page ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setPopupCurrentPage(page)}
                                    className={
                                      popupCurrentPage === page
                                        ? "bg-emerald-600 text-white"
                                        : ""
                                    }
                                  >
                                    {page}
                                  </Button>
                                </React.Fragment>
                              ))}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPopupCurrentPage(prev => Math.min(prev + 1, popupTotalPages))}
                            disabled={popupCurrentPage === popupTotalPages}
                          >
                            Next
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="sticky z-10 bg-white border-t pt-4 flex justify-between">
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>
              Close
            </Button>
            <Button
              onClick={handleAssignCreative}
              disabled={uploadedFiles.length === 0 || isLoading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {isLoading
                ? 'Assigning...'
                : `Assign ${uploadedFiles.length} Creative${uploadedFiles.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>



      {/* View Assigned Creatives Modal */}
      <Dialog open={showAssignedCreativesModal} onOpenChange={setShowAssignedCreativesModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-emerald-600">
              Assigned Creatives for {selectedBillboard?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {billboardAssignedCreatives.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {billboardAssignedCreatives.map((creative) => (
                  <div
                    key={creative.id}
                    className="border rounded-lg p-3 border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <StatusBadge status={creative.status || 'pending'} />
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {creative.format}
                      </span>
                    </div>

                    <div className="aspect-video bg-gray-100 rounded mb-2 overflow-hidden">
                      <img
                        src={creative.thumbnailUrl}
                        alt={creative.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`;
                        }}
                      />
                    </div>

                    <h4 className="text-sm font-medium truncate">{creative.name}</h4>
                    <p className="text-xs text-gray-500">{creative.resolution}</p>
                    {creative.duration !== 'N/A' && (
                      <p className="text-xs text-gray-500">{creative.duration}s</p>
                    )}

                    {/* Comments for rejected status */}
                    {creative.status?.toLowerCase() === 'rejected' && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                        <div className="flex items-start gap-1">
                          <AlertCircle className="h-3 w-3 text-red-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-red-800">Rejection Reason:</p>
                            <p className="text-xs text-red-700">{creative.comments}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No creatives assigned to this billboard yet.
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowAssignedCreativesModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Creative?
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete this creative? This action cannot be undone.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {isLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Creative Preview Modal */}
      <Dialog open={showCreativeModal} onOpenChange={setShowCreativeModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Creative Preview</DialogTitle>
          </DialogHeader>
          {selectedCreativeForView && (
            <div className="space-y-4">
              {/* Creative Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-600">Name:</label>
                  <p className="text-sm text-gray-900 break-words">{selectedCreativeForView.name}</p>
                </div>
                {selectedCreativeForView.status?.toLowerCase() !== 'rejected' && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Status:</label>
                    <div className="mt-1 flex items-center gap-2">
                      <div style={{ height: 32, display: 'flex', boxSizing: 'border-box' }}>
                        <StatusBadge status={selectedCreativeForView.status || 'pending'} />
                      </div>
                    </div>
                  </div>
                )}
                {selectedCreativeForView.status?.toLowerCase() === 'rejected' && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Rejected Reason:</label>
                    <div>
                      <p className="text-sm text-gray-900 whitespace-normal break-words">
                        {selectedRejectionReason}
                      </p>
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-600">Resolution:</label>
                  <p className="text-sm text-gray-900">{selectedCreativeForView.resolution}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Duration:</label>
                  <p className="text-sm text-gray-900">{selectedCreativeForView.duration}</p>
                </div>
              </div>

              {/* Rejection Comments if any */}
              {selectedCreativeForView.status === 'rejected' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                      <p className="text-sm text-red-700 mt-1">{selectedCreativeForView.comments}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Media Preview */}
              <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '400px' }}>
                <div className="w-full max-w-3xl aspect-video bg-black flex items-center justify-center">
                  {selectedCreativeForView.mimeType?.startsWith('video/') ? (
                    <video
                      src={selectedCreativeForView.filePath || getOriginalFileUrl(selectedCreativeForView.thumbnailUrl)}
                      className="w-full h-full object-contain"
                      controls
                      preload="metadata"
                      style={{ background: 'black' }}
                    />
                  ) : (
                    <img
                      src={selectedCreativeForView.filePath || getOriginalFileUrl(selectedCreativeForView.thumbnailUrl)}
                      alt={selectedCreativeForView.name}
                      className="w-full h-full object-contain"
                      style={{ background: 'black' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`;
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Close Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowCreativeModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rejection Reason Modal */}
      <Dialog open={showRejectionReasonModal} onOpenChange={setShowRejectionReasonModal}>
        <DialogContent className="sm:max-w-lg max-w-[90vw]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Creative Rejected Reason
            </DialogTitle>
          </DialogHeader>
          <div>
            <div className="text-center p-4 rounded-lg min-h-[100px]">
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words overflow-wrap-anywhere max-w-full"
                style={{ wordBreak: 'break-word' }} // Fallback for older browsers or if break-words is not defined
              >
                {selectedRejectionReason || 'No reason provided'}
              </p>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <Button onClick={() => setShowRejectionReasonModal(false)} variant="outline">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}